'use client'

import { useRouter } from 'next/navigation'
import { useId, useState, type ComponentType, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import { NextIntlClientProvider } from 'next-intl'
import englishMessages from '@/messages/en.json'
import type { Attachment } from '@/lib/chat-types'
import {
  sokuCreativeAttachments,
  campaignLogAttachment,
  workflowAttachments,
} from './workspace-chat-data'

// The same artifact card and media dialog used by signed-in Chat. Read-only
// mode keeps the marketing preview free of account writes and upsell hooks.
const AgentArtifactCard = dynamic(() =>
  import('@/app/o/[orgSlug]/b/[brandSlug]/(sidebar)/chat/_components/agent-artifact-card').then(
    module => module.AgentArtifactCard,
  ),
)

const WorkspaceAutomations = dynamic(() =>
  import('./workspace-automations').then(module => module.WorkspaceAutomations),
)

const CreativeScenario = dynamic(() =>
  import('./workspace-scenarios').then(module => module.CreativeScenario),
)

const WorkspaceOrganization = dynamic(() =>
  import('./workspace-organization').then(module => module.WorkspaceOrganization),
)
import {
  ArrowUp,
  ArrowLeft,
  ArrowUpLeft,
  ArrowUpRight,
  ArrowDownRight,
  BookOpen,
  Check,
  CheckCircle2,
  AtSign,
  Banknote,
  BarChart4,
  Blocks,
  Building2,
  Calendar,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Coins,
  Command,
  DollarSign,
  Download,
  ExternalLink,
  Eye,
  FilePlus,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Headphones,
  History,
  Images,
  MessageSquare,
  MessagesSquare,
  MousePointerClick,
  Paperclip,
  PanelLeft,
  PanelRightClose,
  PlayCircle,
  PlugZap,
  Puzzle,
  Search,
  ShieldAlert,
  SquarePen,
  Target,
  TrendingDown,
  TrendingUp,
  Upload,
  Wallet,
  X,
  Zap,
} from 'lucide-react'

import { TrackedCtaLink } from '@/components/analytics/tracked-cta-link'
import { track } from '@/lib/analytics/track'
import { cn } from '@/lib/utils'

/** Write-action affordances in the demo link to sign-up: the demo can't run
    the action, but the click intent is exactly the conversion moment. */
function demoCta(text: string) {
  return {
    href: '/sign-up',
    event: 'marketing_cta_click',
    eventProperties: { cta_text: text, cta_location: 'workspace_demo' },
  }
}

/**
 * A replica of the product's shell, for the marketing hero.
 *
 * Traced against the real app at a uniform 0.8 scale — every measurement is
 * the real one × 0.8, not an invention: the shell is `AppSidebarShell`'s dark
 * `--app-shell-frame` ground with the content inset as an `m-2 rounded-xl`
 * card floating on it; the rail is `app-rail.tsx`; the Chat surface is
 * `chat/new/page.tsx` + `chat-history-panel.tsx` + `chat-input.tsx` +
 * `suggestion-cards.tsx` with the real CDN artwork.
 *
 * It is interactive: every rail entry is clickable and swaps the canvas for a
 * demo-data rendering of the REAL surface, each traced from its actual page:
 * - Dashboard → `(sidebar)/dashboard/` (LibraryHeader band, view tabs, KPI
 *   strip with mono numerals + sparklines, metric tiles, trend-by-group chart)
 * - Files → `(sidebar)/files/` (Finder-style: folder tree, amber folders,
 *   doc/video/audio tiles, floating selection bar, docked Chat panel)
 * - Creatives → current list-page hierarchy, product template media and the
 *   shared RemixCanvasPreview (native nodes, edges, playback and zoom).
 * - Automations → the product’s RecommendedAutomations registry and cards,
 *   including the featured ads audit and locally inspectable template prompts.
 * - Organization → the admin scope (`(org-workspace)/`). Entering it swaps the
 *   whole rail menu for the org menu, exactly as the product does on
 *   `/o/{slug}/…`; see `workspace-organization.tsx`.
 * Creative cards use the product template registry and open the actual shared
 * RemixCanvasPreview renderer. Preview interactions do not execute account changes.
 * The working chrome is also real: the rail's panel toggle collapses it to an
 * icon rail outside Chat; Chat keeps its history visible, its search filters the chat
 * list, every chat in it opens a transcript, the composer types, and a pending
 * approval card expands to its operations. Local `useState` only; there is no
 * data fetching. The only navigation is the conversion one — write actions the
 * demo cannot perform (send, approve, reject, attach, open a report) are
 * sign-up CTAs, so nothing in here is a dead click.
 */

type ViewKey =
  | 'chat'
  | 'automations'
  | 'dashboard'
  | 'creatives'
  | 'files'
  | 'integrations'
  | 'skills'
  // Organization Workspace — the admin scope. The product swaps the whole
  // rail menu on these routes (`rail-hrefs.ts`), and so does the demo.
  | 'org-overview'
  | 'org-chats'
  | 'org-activity'

const ORG_VIEWS = ['org-overview', 'org-chats', 'org-activity'] as const

type OrgViewKey = (typeof ORG_VIEWS)[number]

function isOrgView(view: ViewKey): view is OrgViewKey {
  return (ORG_VIEWS as readonly string[]).includes(view)
}

const railItems: {
  key: ViewKey
  label: string
  icon: ComponentType<{ className?: string }>
}[] = [
  { key: 'chat', label: 'Chat', icon: MessageSquare },
  { key: 'automations', label: 'Automations', icon: Zap },
  { key: 'dashboard', label: 'Dashboard', icon: ChartNoAxesCombined },
  { key: 'creatives', label: 'Creatives', icon: Images },
  { key: 'files', label: 'Files', icon: FolderOpen },
]

const railSetupItems: {
  key: ViewKey
  label: string
  icon: ComponentType<{ className?: string }>
}[] = [
  { key: 'integrations', label: 'Integrations', icon: Blocks },
  { key: 'skills', label: 'Skills', icon: Puzzle },
]

/** The org menu the rail swaps to in org scope — the sections the demo
    renders, in the product's own order (`ORG_WORKSPACE_SECTIONS`). */
const orgRailItems: {
  key: OrgViewKey
  label: string
  icon: ComponentType<{ className?: string }>
}[] = [
  { key: 'org-overview', label: 'Overview', icon: Building2 },
  { key: 'org-chats', label: 'Chats', icon: MessagesSquare },
  { key: 'org-activity', label: 'Activity', icon: History },
]

/** Curated excerpts of Soku's production workflows, not simulated outcomes.
 * Sources and artifact transformations are recorded in workspace-chat-data.ts.
 */
const conversationGroups = {
  Examples: [
    'Make ad creatives for Soku',
    'Research competitors → test angles',
    'Build a Meta media plan',
    'Stage a Meta campaign launch',
    'Diagnose Google Ads delivery',
    'Investigate a CPA alert',
    'Duplicate a Meta campaign',
  ],
}

const conversationDates: Record<string, string> = {
  'Research competitors → test angles': 'Aug 20',
  'Build a Meta media plan': 'Sep 7',
  'Stage a Meta campaign launch': 'Sep 7',
  'Diagnose Google Ads delivery': 'Aug 11',
  'Investigate a CPA alert': 'Aug 29',
  'Duplicate a Meta campaign': 'Jul 3',
}

type TranscriptEntry =
  | { role: 'user'; text: string }
  | {
      role: 'assistant'
      text: string
      attachments?: Attachment[]
      /** A pending batch review, rendered as the product's HITL card
          (`batch-review-card.tsx`): the agent is paused until it's decided. */
      batch?: {
        summary: string
        /** Wire action + human summary, exactly the two lines a real
            `ActionReview` operation row shows. */
        ops: { action: string; summary: string }[]
      }
    }

const demoTranscripts: Record<string, TranscriptEntry[]> = {
  // Condensed from Demo Agency's Soku creative thread (English twin in
  // seed_demo_org.py). The attached PNGs are the exact production assets.
  'Make ad creatives for Soku': [
    {
      role: 'user',
      text: 'Generate a Meta batch for us (Soku) — autopilot angle, mix up the styles. Make sure every one carries the logo and a CTA.',
    },
    {
      role: 'assistant',
      text: 'Here are the 6 finished creatives, all 1024 × 1024: three editorial directions, a product-proof direction, a testimonial layout and a lifestyle direction. Logo and CTA are included. Pick a direction and I can make A/B variants, adapt it for a Google PMax asset set, or turn it into a vertical video.',
      attachments: sokuCreativeAttachments,
    },
  ],
  'Research competitors → test angles': [
    {
      role: 'user',
      text: 'Go deeper on Birch / Revealbot in the US: what are they advertising, where does the traffic land, and what does that tell us about their acquisition strategy?',
    },
    {
      role: 'assistant',
      text: 'The August 20 research combines 22 Google ad records (10 search text, 12 display), 30 ranked-keyword samples and 5 website pages. The full report includes 3 analytical charts, 5 ad/page captures, source links and evidence limits.',
      attachments: [workflowAttachments.research],
    },
    {
      role: 'assistant',
      text: 'Search and display tell different stories. Every ad with more than 600 days of archive history was a search text ad; none of the 12 display ads exceeded 53 days. Search still uses Revealbot branding, while the display capture promotes “Try Birch 14 Days Free.” These are archive observations, not spend or conversion results.',
      attachments: [
        workflowAttachments.searchAd,
        workflowAttachments.displayAd,
      ],
    },
    {
      role: 'user',
      text: 'What are the actual acquisition paths behind those ads?',
    },
    {
      role: 'assistant',
      text: 'Two paths are supported by the evidence: CPM/CPC questions → weekly updated cost benchmark → free trial; competitor-name intent → AdEspresso/Madgicx alternative page → free guide. Cost terms account for 25 of 30 sampled keywords, not 83% of total traffic. The AI product takes a third route: supervised co-pilot positioning → closed-beta application.',
    },
    {
      role: 'user',
      text: 'Turn those findings into specific tests we can evaluate.',
    },
    {
      role: 'assistant',
      text: 'Proposed follow-up: test a dedicated comparison page for switching intent, a useful account diagnostic for cost questions, and a real approval-flow walkthrough for trust. The brief specifies the evidence, variable to test, destination and qualified-conversion measures for each. These are proposed experiments, not launched campaigns or proven winners.',
      attachments: [workflowAttachments.researchBrief],
    },
  ],
  'Build a Meta media plan': [
    {
      role: 'user',
      text: 'Plan Meta for US and Canada at $15,000/month. Anchor the targets to our conversion history and include the creative plan.',
    },
    {
      role: 'assistant',
      text: 'The workbook includes campaign structure, five creative angles and a budget plan. The proposed $500/day splits into $150 testing, $200 conversion and $150 retargeting.',
      attachments: [workflowAttachments.mediaPlan],
    },
    {
      role: 'assistant',
      text: 'The historical CPA anchor is $16.27, based on just $119.53 spend and 7.35 attributed conversions over 90 days. That is a thin baseline for this budget increase. Treat the workbook’s volume estimates as planning assumptions and validate CPA as delivery scales.',
    },
  ],
  'Stage a Meta campaign launch': [
    {
      role: 'user',
      text: 'We haven’t finished all the creatives. Deploy the structure using the image we just made, and keep everything paused.',
    },
    {
      role: 'assistant',
      text: '3 campaigns, 4 ad sets and 4 ads were staged in PAUSED state. Earlier batches partially failed; after replacing the instant-form creative with a Website Lead creative, the final four ad attachments succeeded.',
      attachments: [workflowAttachments.deployment],
    },
    {
      role: 'assistant',
      text: 'The first image is attached across the structure. The remaining testing angles and retargeting video still need production. This completed the staged setup; it did not activate delivery.',
    },
  ],
  'Diagnose Google Ads delivery': [
    {
      role: 'user',
      text: 'Audit campaign structure, keywords, conversion tracking and budget pacing. Rank the fixes by impact.',
    },
    {
      role: 'assistant',
      text: 'The account was switched off: all 21 campaigns paused, with no impressions for 41 days. This was a restart problem. In the last delivery window, GrowthLead brought 83 conversions at $28.79; Agency spent $591.92 for one.',
      attachments: [workflowAttachments.diagnostic],
    },
    {
      role: 'assistant',
      text: 'Fix the signal and serving blockers before restarting: 320 lead events were excluded from the bidding conversion metric, and 8 campaigns had 108 enabled keywords but no enabled ads. All 297 Quality Scores were unavailable after the dark period, so the report leaves that diagnosis open.',
    },
  ],
  'Investigate a CPA alert': [
    {
      role: 'user',
      text: 'Autopilot trigger: Google Ads CPA has crossed its threshold. Investigate and decide the next step.',
    },
    {
      role: 'assistant',
      text: '$74.72 CPA looks alarming, but it comes from only 2 delivery days and 3 conversions after 58 days of account inactivity. Search intent was on target and tracking was recording conversions.',
      attachments: [workflowAttachments.cpaDecision],
    },
    {
      role: 'assistant',
      text: 'No optimization proposal was submitted. The evidence was too thin to justify pausing the new campaign. The monitor was deferred to September 3, allowing 5–7 delivery days before reassessing.',
    },
  ],
  // Historical production batch, not a forecast. Source:
  // seed_demo_org.py::_SOKU_DUP_BATCH_SUMMARY / _SOKU_DUP_LOG_MD_EN.
  'Duplicate a Meta campaign': [
    {
      role: 'user',
      text: 'Duplicate SOKU_Meta_LeadGen_USP1_CostCap20_20260629_01. Keep the targeting and creatives. Leave everything paused.',
    },
    {
      role: 'assistant',
      text: '46/46 operations completed and verified: 1 campaign, 3 ad sets, 21 creatives and 21 ads. All ads are in ONAD_Page; the other two ad sets are empty, matching the source. Everything is PAUSED.',
      attachments: [campaignLogAttachment],
    },
    {
      role: 'assistant',
      text: 'Before activation: restore and verify the $20 cost cap in Ads Manager. In this run, the handler fell back to LOWEST_COST_WITHOUT_CAP. Targeting is US, ages 25–45; each ad set retains its $20/day budget.',
    },
  ],
}

const CDN = 'https://cdn.soku.ai/marketing/chat'
const REMIX_CDN = 'https://cdn.soku.ai/marketing/remix-demo'

/** Verbatim from `Suggestions.tabs` / `Suggestions.cardDescriptions`. */
const categories = [
  {
    title: 'ChatGPT Ads',
    description: 'Launch on the newest ad channel',
    image: `${CDN}/cat-chatgpt-v1.jpg`,
  },
  {
    title: 'Meta Ads',
    description: 'Creatives, audiences, budget moves',
    image: `${CDN}/cat-meta-v1.jpg`,
  },
  {
    title: 'Google Ads',
    description: 'Search intent, bids, quality score',
    image: `${CDN}/cat-google-v1.jpg`,
  },
  {
    title: 'Research',
    description: 'Competitors, keywords, market maps',
    image: `${CDN}/cat-research-v1.jpg`,
  },
  {
    title: 'SEO & GEO',
    description: 'Rank in Google, get cited by AI',
    image: `${CDN}/cat-seo-v1.jpg`,
  },
  {
    title: 'Creative',
    description: 'Hooks, angles, fresh variants',
    image: `${CDN}/cat-creative-v1.jpg`,
  },
  {
    title: 'Analytics',
    description: "What moved, why, what's next",
    image: `${CDN}/cat-analytics-v1.jpg`,
  },
  {
    title: 'Automation',
    description: 'Reports that run themselves',
    image: `${CDN}/cat-automation-v1.jpg`,
  },
  {
    title: 'Landing Pages',
    description: 'Pages that catch the click',
    image: `${CDN}/cat-landing-v1.jpg`,
  },
]

/**
 * Real prompts, verbatim from `Suggestions.cards` in `messages/en.json` —
 * the same per-category lists the product's suggestion panel shows.
 * Clicking a category card expands them, clicking one fills the composer,
 * exactly like `suggestion-cards.tsx`.
 */
const suggestionPrompts: Record<string, { title: string; prompt: string }[]> = {
  'ChatGPT Ads': [
    {
      title: 'How are our ChatGPT Ads performing?',
      prompt:
        'Pull our ChatGPT Ads performance for the last 14 days — impressions, clicks, spend, and CPM by campaign — and tell me what stands out.',
    },
    {
      title: 'Plan our first ChatGPT Ads campaign',
      prompt:
        "We want to test ChatGPT Ads. Draft a launch plan: which products to promote, which landing pages to send traffic to, a starting budget, and how we'll measure success.",
    },
    {
      title: 'Compare ChatGPT Ads vs Google and Meta',
      prompt:
        'Compare our ChatGPT Ads results against Google and Meta on CPM, CTR, and conversion rate, and recommend where the next $1,000 should go.',
    },
    {
      title: 'Get our landing pages ready for ChatGPT Ads',
      prompt:
        'Review the landing pages behind our ChatGPT Ads campaigns and flag anything that would hurt conversion for AI-referred visitors — then propose fixes.',
    },
    {
      title: 'Set up a weekly ChatGPT Ads report',
      prompt:
        'Every Monday morning, send me a summary of our ChatGPT Ads spend and performance with week-over-week changes and anything that needs attention.',
    },
    {
      title: 'Draft creative angles for ChatGPT Ads',
      prompt:
        'Suggest 5 ad angles tailored to how people ask ChatGPT about our category, with a headline and one-line description for each.',
    },
    {
      title: 'Where should I move ChatGPT Ads budget?',
      prompt:
        'Look at our ChatGPT Ads campaigns and tell me which to scale, which to pause, and how to reallocate budget for the best return next week.',
    },
    {
      title: 'Find new audiences for ChatGPT Ads',
      prompt:
        'Based on who converts on our other channels, propose new audiences and prompt themes to target with ChatGPT Ads.',
    },
  ],
  'Meta Ads': [
    {
      title: 'Duplicate a Winning Campaign With New Creatives',
      prompt:
        'Copy our best-performing Meta campaign from last month, keep the same audiences and budget split, but swap in the new product creatives and rewrite the primary text for a summer angle.',
    },
    {
      title: 'Launch a Meta Campaign From a One-Line Brief',
      prompt:
        'Launch a Meta conversion campaign for our new skincare serum targeting US women 25-44, $100/day budget, ROAS goal 3:1. Draft the audiences, write the copy, and show me everything before it goes live.',
    },
    {
      title: 'Diagnose and Fix Rejected Meta Ads',
      prompt:
        'Three of our Meta ads got rejected this morning. Figure out why, fix the copy or creative so they comply, and resubmit them.',
    },
    {
      title: 'Bulk-Edit Copy Across Every Live Ad',
      prompt:
        'We can no longer mention the $49 price or the 3-day minimum stay in any ad. Find every live ad that mentions either, rewrite the copy without them, and push the updates.',
    },
    {
      title: 'Build a Custom Audience From Your Data',
      prompt:
        "Create a custom audience of people who visited our pricing page in the last 30 days but didn't sign up, and build 1% and 3% lookalikes from our purchasers for prospecting.",
    },
    {
      title: 'Roll a Winning Ad Set Out to New Geos',
      prompt:
        'Our US ad set is hitting target CPA. Duplicate it for the UK, Canada, and Australia with budgets scaled to audience size, and adjust any US-specific copy.',
    },
    {
      title: 'Verify Pixel and Conversion Events',
      prompt:
        'Check whether our Meta pixel and conversion events are firing correctly on the new checkout flow, and tell me if purchases are being double-counted or dropped.',
    },
    {
      title: 'Set Up a Clean A/B Test',
      prompt:
        "Set up an A/B test on Meta: same audience and budget, creative A vs creative B. Tell me how long it needs to run for significance and report the winner when it's decided.",
    },
    {
      title: "Reallocate Budgets Toward What's Working",
      prompt:
        "Review all our active campaigns' ROAS for the last 14 days and propose a budget reallocation — move spend from anything below 1.5 ROAS to the winners. Show me the plan before applying.",
    },
    {
      title: 'Pause Fatigued Ads Before They Drag CPA',
      prompt:
        'Find ads where frequency is above 4 and CTR has dropped more than 30% from their peak, pause them, and suggest which creatives from our library should replace them.',
    },
    {
      title: 'Connect and Map Dozens of Client Accounts',
      prompt:
        "Show me all the Meta ad accounts we're connected to, map each one to the right client brand, and flag any accounts that stopped syncing or lost permissions.",
    },
    {
      title: 'Standardize UTM Tags Across Every Ad',
      prompt:
        'Audit the UTM parameters on every live ad across Meta and Google. Fix anything missing or inconsistent so our GA4 channel reporting is clean, and document the convention.',
    },
  ],
  'Google Ads': [
    {
      title: 'Full Google Ads Account Diagnostic',
      prompt:
        'Run a full diagnostic on our Google Ads account: campaign structure, keyword health, quality scores, conversion tracking, and budget pacing. Rank the fixes by impact.',
    },
    {
      title: 'Search Terms Quality Audit',
      prompt:
        'Audit our Google Ads search terms for the last 30 days. How much are we spending on junk queries, which terms deserve their own ad groups, and what should be negatives?',
    },
    {
      title: 'Full-Month Paid Spend Review',
      prompt:
        'Do a full-month review of all our paid spend across Google and Meta: total spend, what actually drove conversions, what underperformed, and your budget recommendation for next month.',
    },
    {
      title: 'Quality Score Deep-Dive and Fix Plan',
      prompt:
        'Our Google Ads quality scores are dragging. Break them down by component for each ad group and give me the specific ad copy and landing page fixes that will raise them.',
    },
    {
      title: 'Performance Max Campaign Audit',
      prompt:
        'Audit our Performance Max campaigns: which asset groups work, where the spend actually goes by channel, and whether PMax is cannibalizing our brand search terms.',
    },
    {
      title: 'Conversion Tracking Audit',
      prompt:
        'Run a conversion tracking audit: compare what Google Ads and Meta report against GA4 and our actual signups. Find any double-counting or missing events and fix the configuration.',
    },
    {
      title: 'Expand Keywords From What Already Converts',
      prompt:
        "Find new keywords from our converting search terms in the last 90 days — things we're matching on but haven't broken out. Check volumes and add the good ones to the right ad groups.",
    },
    {
      title: 'Geo Performance Breakdown and Reallocation',
      prompt:
        'Break down our ad performance by geography. Which countries and cities are actually profitable? Exclude or down-bid the money losers and shift budget to the winners.',
    },
    {
      title: 'Fix Mobile vs Desktop Performance Gaps',
      prompt:
        "Our mobile CPA is 3x desktop. Break down performance by device, figure out whether it's the landing page or the traffic, and set the right device bid adjustments.",
    },
    {
      title: 'Launch a Google Search Campaign From Keywords',
      prompt:
        'Build a Google Search campaign for our plumbing service in Austin: tight ad groups from these seed keywords, responsive ads with call extensions, and conversion tracking on the booking form.',
    },
    {
      title: 'Apply Negative Keywords From Wasted Spend',
      prompt:
        "Go through our Google Ads search terms from the last 30 days, find the irrelevant queries we're paying for, and add them as negative keywords — grouped into shared lists where it makes sense.",
    },
    {
      title: "Reallocate Budgets Toward What's Working",
      prompt:
        "Review all our active campaigns' ROAS for the last 14 days and propose a budget reallocation — move spend from anything below 1.5 ROAS to the winners. Show me the plan before applying.",
    },
  ],
  Research: [
    {
      title: "Spy on a Competitor's Meta Ads",
      prompt:
        'Pull everything [competitor] is running in the Meta Ad Library. Which ads have been live longest, what angles and offers are they pushing, and what should we test in response?',
    },
    {
      title: "Analyze a Competitor's Google Ads Strategy",
      prompt:
        "Analyze [competitor]'s Google Ads presence: what keywords are they bidding on, what does their copy promise, and which of their profitable terms are we missing?",
    },
    {
      title: 'Full Competitive Brief on One Rival',
      prompt:
        'Build a full competitive brief on [competitor]: product, pricing, positioning, where they advertise, their SEO strength, and their social presence. End with the 3 biggest openings for us.',
    },
    {
      title: 'Decode Competitor TikTok Creative Strategy',
      prompt:
        "Find [competitor]'s best-performing TikTok ads, break down their hooks and formats, and write 3 creative briefs that beat them at their own pattern.",
    },
    {
      title: 'SEO Keyword Gap vs Competitors',
      prompt:
        "Compare our ranking keywords against [competitor A] and [competitor B]. Where do they rank that we don't? Prioritize the gaps by traffic value and difficulty.",
    },
    {
      title: 'Competitor Landing Page Teardown',
      prompt:
        "Tear down [competitor]'s main landing pages: their offer, page structure, social proof, and CTAs. What's working that we should adopt, and where can we differentiate?",
    },
    {
      title: 'Positioning and Messaging Comparison',
      prompt:
        'Map the positioning of the top 6 players in our category — taglines, key claims, target customers. Where is everyone saying the same thing, and what position is open for us?',
    },
    {
      title: "Discover Competitors You Don't Know About",
      prompt:
        "Who are we actually competing with? Search our top keywords, check who bids against us in ad auctions, and scan review sites. List anyone taking our customers that we're not tracking.",
    },
    {
      title: 'Check Your Visibility in AI Search',
      prompt:
        'When people ask ChatGPT and Perplexity for tools in our category, who gets recommended? Check our visibility in AI answers vs competitors and tell me how to improve it.',
    },
    {
      title: 'Track Competitor Pricing and Packaging Changes',
      prompt:
        "Compare our pricing and packaging against [competitor A], [B], and [C]. Where are we mispriced, and what does their plan structure tell us about who they're targeting?",
    },
    {
      title: "See Who's Bidding on Your Brand",
      prompt:
        "Search our brand name and our top product terms — who's running ads against them? Show me the competitor ads targeting our brand and give me a defense plan.",
    },
    {
      title: "Analyze Competitors' LinkedIn Playbook",
      prompt:
        'Analyze the LinkedIn presence of [competitor] and their founders: what do they post, what gets engagement, how often? Draft us a 2-week plan that beats their playbook.',
    },
  ],
  'SEO & GEO': [
    {
      title: 'Get our pages cited by ChatGPT and Perplexity',
      prompt:
        'Audit our top 10 pages for GEO: clear definitions, quotable stats, FAQs, and schema markup — then rewrite the weakest sections so AI engines cite us as the source.',
    },
    {
      title: 'Track our share of AI answers vs competitors',
      prompt:
        'For our 20 core buyer questions, check who ChatGPT, Perplexity, and Google AI Overviews cite today — us or competitors — and give me a plan to win the answers we are losing.',
    },
    {
      title: 'Optimize Content for AI Answers (AEO)',
      prompt:
        'Optimize our top 10 pages for AI search: add direct answer blocks, FAQ schema, and citable stats so ChatGPT and Perplexity start referencing us for category questions.',
    },
    {
      title: 'Find Quick Wins in Search Console',
      prompt:
        "Pull our Search Console data and find the quick wins: keywords ranking 5-15, page-1 results with bad CTR, and titles that don't match what people actually search. Fix the on-page issues.",
    },
    {
      title: 'Keyword Research Into a 90-Day Content Plan',
      prompt:
        'Do keyword research for our product category, cluster by intent, and build a 90-day content calendar — quick-win pages first, pillar content after.',
    },
    {
      title: 'Technical SEO Audit',
      prompt:
        'Run a technical SEO audit on our site: indexing, sitemaps, redirects, broken links, and page speed. Prioritize by how much traffic each issue puts at risk.',
    },
    {
      title: 'Refresh Decaying Content',
      prompt:
        'Which of our pages are losing traffic? Diagnose why each one is decaying and refresh the top 5 — updated data, better intent match, stronger internal links.',
    },
    {
      title: 'Design a Programmatic SEO Play',
      prompt:
        'Design a programmatic SEO play for us: what repeatable page pattern (comparisons, integrations, use cases, locations) fits our product, and what would the template and data look like?',
    },
    {
      title: 'Fix Internal Linking Structure',
      prompt:
        'Map our internal linking: find orphan pages and money pages starved of links. Add contextual internal links from our strongest pages to the ones we need ranking.',
    },
    {
      title: 'Research and Write a Ranking Blog Post',
      prompt:
        'Write a blog post targeting [keyword]. Study what currently ranks, cover what they miss, include original angles from our product experience, and format it to compete for the featured snippet.',
    },
    {
      title: 'Backlink Profile Analysis and Link Plan',
      prompt:
        'Analyze our backlink profile vs [competitor]. Who links to them but not us? Build a prioritized outreach list of realistic link targets with the angle for each.',
    },
    {
      title: 'Plan and Draft ICP Landing Pages',
      prompt:
        'We want to test our ICP split — buyers vs users. What pages would you create for each? Plan the set, then draft all 8 pages with titles, meta, and body copy.',
    },
  ],
  Creative: [
    {
      title: 'Turn a Product Photo Into a Video Ad',
      prompt:
        "Here's a photo of our product. Turn it into a 15-second video ad for Meta and TikTok — show it in use, add a hook in the first 2 seconds, and end with our discount code.",
    },
    {
      title: 'Generate New Variants From Your Winning Ad',
      prompt:
        'Our top ad is fatiguing. Analyze why it worked and generate 3 new variants: same angle with new visuals, new angle with the same format, and one wildcard.',
    },
    {
      title: 'Write a Batch of UGC Scripts',
      prompt:
        'Write 10 UGC scripts for our product across different frameworks: problem-agitate-solve, 3 reasons why, day-in-the-life, and unboxing. Include hook lines and b-roll notes for creators.',
    },
    {
      title: 'Adapt One Creative to Every Placement',
      prompt:
        'Take this hero creative and adapt it for every placement: square feed, 9:16 Stories and Reels, TikTok with native-style captions, and the standard display sizes.',
    },
    {
      title: 'Localize Ad Copy for New Markets',
      prompt:
        'Localize our 5 best-performing ads for the Saudi market: Arabic copy that actually sounds native, culturally right visuals notes, and offers that fit local buying habits.',
    },
    {
      title: 'Build a Hook Library for Your Product',
      prompt:
        'Build us a hook library: 50 first-lines for ads and videos across curiosity, controversy, social proof, and bold-outcome styles. Rank the top 10 for our audience.',
    },
    {
      title: 'Generate Static Ads From Your Landing Page',
      prompt:
        'Read our landing page and generate 6 static ad concepts from it — pull the strongest claims and testimonials, keep our brand colors and fonts, and write matching headlines.',
    },
    {
      title: 'Produce a Seasonal Campaign Creative Set',
      prompt:
        'Build our Black Friday creative set: one strong concept carried across video, statics, Stories, and email headers, with countdown and last-chance variants ready to schedule.',
    },
    {
      title: 'Turn Customer Reviews Into Testimonial Ads',
      prompt:
        'Go through our reviews, pull the 10 most persuasive quotes, match each to the objection it kills, and design testimonial ads around the best 5.',
    },
    {
      title: 'Design Carousel Ads for a Product Line',
      prompt:
        'Design 3 carousel ads for our product line: one story-driven sequence, one bestseller lineup, one before/after. Write the per-card copy and headline for each.',
    },
    {
      title: 'Generate On-Brand Images at Scale',
      prompt:
        'Learn our brand style from our website and these assets, then generate 12 campaign images for our new feature launch that look unmistakably like our brand.',
    },
    {
      title: 'Recut a Long Video Into Ad-Length Clips',
      prompt:
        "Here's our 20-minute product demo. Cut it into 5 ad-length clips around the strongest moments, add captions, and format them for Reels and TikTok.",
    },
  ],
  Analytics: [
    {
      title: 'Monday Morning Performance Report',
      prompt:
        "Every Monday morning, pull last week's performance across Google, Meta, and GA4 — spend, conversions, CPA, ROAS vs the week before — and send me a summary that explains what changed and why.",
    },
    {
      title: 'GA4 Funnel Drop-Off Analysis',
      prompt:
        "Analyze our signup funnel in GA4: where's the biggest drop-off? Break it down by traffic source, device, and country so we know exactly which segment is leaking.",
    },
    {
      title: 'One Report Across Every Channel',
      prompt:
        "Build me one weekly view across Google Ads, Meta, TikTok, and organic: consistent spend, CAC, and ROAS definitions, with each channel's trend and share of new customers.",
    },
    {
      title: 'Compare Attribution Models Honestly',
      prompt:
        'Compare our channel performance under last-click vs data-driven attribution vs what the platforms self-report. Where do they disagree most, and what should we trust for budget decisions?',
    },
    {
      title: 'Client-Ready Monthly Report',
      prompt:
        "Generate this month's client report for [client]: performance vs their goals, the work we shipped, three insights they'll care about, and next month's plan. Make it presentable enough to send directly.",
    },
    {
      title: 'Explain Any Metric Anomaly',
      prompt:
        'Our organic traffic dropped 25% on Tuesday. Trace it: which pages, which queries, which geography — and was it an algorithm update, tracking issue, or something we changed?',
    },
    {
      title: 'LTV and Cohort Analysis by Channel',
      prompt:
        'Run a cohort analysis: LTV at 30/90/180 days by acquisition channel. Which channels bring customers who actually stick around, and how should that change our CAC targets?',
    },
    {
      title: 'Creative Performance Matrix',
      prompt:
        'Build a creative performance matrix from our last 90 days: format × hook type × length against CPA and hook rate. Which combinations should the next creative batch use?',
    },
    {
      title: 'Lead Quality by Source Report',
      prompt:
        'Analyze lead quality by source: which campaigns produce leads that actually close vs just form fills? Score each source and tell me where to shift budget.',
    },
    {
      title: 'Budget Scenario Planning',
      prompt:
        'If we increased ad spend 50%, where should it go? Model scenarios from our actual data: which campaigns have headroom and which are already hitting diminishing returns.',
    },
    {
      title: 'Board-Ready Growth Digest',
      prompt:
        "Prepare the marketing section for our board deck: this month's growth drivers, CAC and efficiency trends, what's working, what's at risk, and what we're doing about it.",
    },
    {
      title: 'Connect Marketing Spend to Product Behavior',
      prompt:
        'Connect our ad data to PostHog: which campaigns drive users who actually activate and are still active at day 30? Rank campaigns by activated-user CAC, not signup CAC.',
    },
  ],
  Automation: [
    {
      title: 'Daily Budget Pacing Watchdog',
      prompt:
        'Every morning, check spend pacing across all our accounts against monthly budgets. Alert me if any account is projected to overshoot or undershoot by more than 10%.',
    },
    {
      title: 'Disapproved Ads Instant Alert',
      prompt:
        'Monitor all our ad accounts for disapprovals and policy flags. When something gets rejected, alert me immediately with the reason and a suggested compliant rewrite.',
    },
    {
      title: 'Weekly Competitor Ads Digest',
      prompt:
        'Every Friday, check what our top 5 competitors launched in their ad libraries this week — new creatives, new angles, new offers — and send me a digest.',
    },
    {
      title: 'Keyword Rank Movement Alerts',
      prompt:
        "Track our top 30 keywords daily. Alert me when anything moves more than 3 positions — ours or a competitor's — and include what probably caused it.",
    },
    {
      title: 'Detect Stalled Campaigns Before They Cost You',
      prompt:
        "Watch our campaigns for delivery problems: if any campaign's spend stalls or drops sharply, diagnose whether it's payments, audience saturation, or bidding, and notify me with the fix.",
    },
    {
      title: 'Guardrail Rules That Pause Losers',
      prompt:
        'Set up guardrails: pause any ad that spends $150 with no conversion, alert me at frequency above 5, and stop all traffic to any landing page that starts erroring.',
    },
    {
      title: 'Weekly Search Terms Sweep',
      prompt:
        'Every week, sweep our Google Ads search terms: add obvious junk as negatives automatically, and send me anything ambiguous plus new keyword opportunities worth breaking out.',
    },
    {
      title: 'Weekly Site and SEO Health Check',
      prompt:
        'Run a weekly health check on our site: broken links, deindexed pages, Core Web Vitals regressions, and new Search Console errors. Only ping me when something needs action.',
    },
    {
      title: 'Brand Mention and Review Tracker',
      prompt:
        'Track mentions of our brand across reviews, Reddit, and social. Send a weekly digest, but alert me immediately for anything negative gaining traction.',
    },
    {
      title: 'Instant Lead Alerts With Context',
      prompt:
        'When a new lead submits our form, enrich it with their company and role, note which campaign and ad brought them, and post it to our sales channel with a suggested first touch.',
    },
    {
      title: 'Scheduled Creative Refresh Pipeline',
      prompt:
        "Every two weeks: review which creatives are fatiguing, generate a new batch based on the current winners' patterns, and queue them for my approval before launch.",
    },
    {
      title: 'Turn Any Workflow Into a Reusable Skill',
      prompt:
        'That competitive analysis you just ran was exactly what we need monthly. Turn it into a reusable skill so anyone on the team can run it for any client.',
    },
  ],
  'Landing Pages': [
    {
      title: 'Build a Landing Page From a Brief',
      prompt:
        'Build a landing page for our webinar campaign: headline options, section structure, speaker credibility, and a registration form short enough that people finish it.',
    },
    {
      title: 'Landing Page Conversion Audit',
      prompt:
        'Audit our main landing page for conversion: message clarity, friction points, trust signals, load speed. Score it and rank the fixes by expected lift.',
    },
    {
      title: 'Build a Prioritized A/B Test Backlog',
      prompt:
        'Look at our funnel data and landing pages, generate 15 A/B test hypotheses, and prioritize them by ICE score into a testing backlog for this quarter.',
    },
    {
      title: 'Landing Page Variants Matched to Each Ad Angle',
      prompt:
        "We run 4 ad angles into one generic page. Create a landing page variant matched to each angle — same structure, but headline and proof continuing each ad's specific promise.",
    },
    {
      title: 'Find Friction in Your Signup Flow',
      prompt:
        'Analyze our signup flow in PostHog: where do users stall, rage-click, or drop? Diagnose the top 3 friction points and propose the redesign for each.',
    },
    {
      title: 'Pricing Page Teardown and Redesign',
      prompt:
        "Tear down our pricing page: anchoring, plan comparison clarity, objection handling. Redesign it to guide buyers to the mid-tier plan and reduce 'contact sales' dead-ends.",
    },
    {
      title: 'Optimize Lead Forms for Completion',
      prompt:
        'Our demo request form has 9 fields. Analyze which fields cause abandonment, which we actually use in sales, and redesign it for completion without wrecking lead quality.',
    },
    {
      title: 'Place Social Proof Where It Converts',
      prompt:
        'Audit all our social proof assets and our funnel. Place the right proof at each objection point — which testimonial near pricing, which logos on the landing page, which numbers in ads.',
    },
    {
      title: 'Fix Page Speed Where It Costs Conversions',
      prompt:
        'Which of our pages are slow enough to cost conversions? Cross page speed against bounce and conversion data, find the worst offenders on paid routes, and fix them.',
    },
    {
      title: 'Mobile Experience Audit',
      prompt:
        'Audit our mobile experience end-to-end: landing page, navigation, forms, checkout. Most of our paid traffic is mobile — find everything that desktop-first design broke.',
    },
    {
      title: 'Design a Quiz Funnel',
      prompt:
        'Design a quiz funnel for our skincare brand: questions that segment by skin type and concern, personalized product recommendations, and email capture before results.',
    },
    {
      title: 'Make Thank-You Pages Do More Work',
      prompt:
        'Our thank-you pages are dead ends. Redesign each one with the right next step: booking a call after demo requests, referral after purchase, content after newsletter signup.',
    },
  ],
}

const scenarios = [
  {
    key: 'chat',
    label: 'Chat',
  },
  {
    key: 'creatives',
    label: 'Creatives',
  },
  {
    key: 'dashboard',
    label: 'Dashboard',
  },
  {
    key: 'automations',
    label: 'Automations',
  },
  {
    // The admin scope. Its tab lands on Activity — the audit feed is the
    // answer to "who told the agent to do that", which is the reason an
    // agency asks to see the organization view at all.
    key: 'org-activity',
    label: 'Organization',
    views: ORG_VIEWS,
  },
] as const

export function WorkspaceDemo({ className }: { className?: string }) {
  return (
    <NextIntlClientProvider
      locale="en"
      timeZone="UTC"
      messages={englishMessages}
    >
      <WorkspaceDemoContent className={className} />
    </NextIntlClientProvider>
  )
}

function WorkspaceDemoContent({ className }: { className?: string }) {
  const [view, updateView] = useState<ViewKey>('chat')
  const [canvasOpen, setCanvasOpen] = useState(false)
  const setView = (next: ViewKey) => {
    if (next === view) return
    updateView(next)
    if (next === 'chat') setRailCollapsed(false)
    setCanvasOpen(false)
  }
  const demoId = useId()
  const scenario = scenarios.find(
    item =>
      item.key === view ||
      ('views' in item && (item.views as readonly string[]).includes(view)),
  )

  const [railCollapsed, setRailCollapsed] = useState(false)

  return (
    <div className={cn('w-full', className)}>
      <div className="mb-4 flex justify-center">
        <div
          role="tablist"
          aria-label="Product scenarios"
          className="flex w-full max-w-full gap-1.5 overflow-x-auto rounded-full border border-white/15 bg-black/20 p-1.5 sm:w-auto"
        >
          {scenarios.map((item, index) => {
            const { key, label } = item
            // A scenario whose surface has sub-views (the org menu) stays
            // selected while the rail moves inside it.
            const selected = scenario?.key === key
            return (
              <button
                key={key}
                id={`${demoId}-${key}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`${demoId}-panel`}
                tabIndex={selected || (!scenario && index === 0) ? 0 : -1}
                onClick={() => setView(key)}
                onKeyDown={event => {
                  const next =
                    event.key === 'ArrowRight'
                      ? (index + 1) % scenarios.length
                      : event.key === 'ArrowLeft'
                        ? (index + scenarios.length - 1) % scenarios.length
                        : event.key === 'Home'
                          ? 0
                          : event.key === 'End'
                            ? scenarios.length - 1
                            : null
                  if (next === null) return
                  event.preventDefault()
                  setView(scenarios[next].key)
                  document
                    .getElementById(`${demoId}-${scenarios[next].key}`)
                    ?.focus()
                }}
                className={cn(
                  'flex shrink-0 cursor-pointer items-center rounded-full px-5 py-2.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-orange-400',
                  selected
                    ? 'bg-[#fff9ed] text-neutral-900 shadow-sm'
                    : 'text-white/75 hover:bg-white/10 hover:text-white',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>
      <div
        role="tabpanel"
        id={`${demoId}-panel`}
        aria-labelledby={scenario ? `${demoId}-${scenario.key}` : undefined}
        aria-label={scenario ? undefined : 'Workspace preview'}
        tabIndex={0}
      >
        {/* The dark app-shell frame. */}
        <div className="flex h-[520px] w-full overflow-hidden rounded-2xl bg-[oklch(0.278_0.005_75)] text-left shadow-[0_40px_120px_rgba(4,8,20,0.55)] ring-1 ring-black/15 sm:h-[600px] lg:h-[680px]">
          {/* Rail — `app-rail.tsx` at 0.8: 200px → 172px, 14px rows → 12px.
          The panel toggle really collapses it to an icon rail, like Cmd+B in
          the product: labels drop, icons keep their left x. */}
          {!canvasOpen && (
            <aside
              className={cn(
                'hidden shrink-0 flex-col p-2 pb-2.5 text-[oklch(0.95_0.004_80)] transition-[width] duration-200 ease-linear sm:flex',
                railCollapsed ? 'w-[46px]' : 'w-[172px]',
              )}
            >
              <div className="flex items-center justify-between px-1 pt-1">
                {railCollapsed ? null : (
                  <span className="flex items-center gap-1.5 pl-0.5">
                    <img
                      src="/soku-logo-square.png"
                      alt="Soku"
                      className="size-4.5 shrink-0"
                    />
                    <span className="translate-y-[1px] font-serif text-[16px] leading-none font-normal tracking-tight text-[#ff7700]">
                      Soku
                    </span>
                  </span>
                )}
                {view !== 'chat' && (
                  <button
                    type="button"
                    aria-label={
                      railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                    }
                    onClick={() => setRailCollapsed(collapsed => !collapsed)}
                    className="flex size-5.5 cursor-pointer items-center justify-center rounded-md opacity-50 transition-opacity hover:bg-white/10 hover:opacity-90"
                  >
                    <PanelLeft className="size-3.5" />
                  </button>
                )}
              </div>

              <nav className="mt-3 flex flex-col gap-0.5">
                {/* Org scope swaps the whole brand menu for the org menu,
                    exactly as `app-rail.tsx` does on `/o/{slug}/…` routes —
                    with the same way back, so the switch is never a trap. */}
                {isOrgView(view) ? (
                  <>
                    <RailItem
                      label="Back to brand"
                      icon={ArrowLeft}
                      active={false}
                      collapsed={railCollapsed}
                      onClick={() => setView('chat')}
                      className="opacity-60"
                    />
                    <span className="my-1 h-px bg-white/12" />
                    {orgRailItems.map(item => (
                      <RailItem
                        key={item.key}
                        label={item.label}
                        icon={item.icon}
                        active={view === item.key}
                        collapsed={railCollapsed}
                        onClick={() => setView(item.key)}
                      />
                    ))}
                  </>
                ) : (
                  <>
                  {railItems.map(item => (
                    <RailItem
                      key={item.key}
                      label={item.label}
                      icon={item.icon}
                      active={view === item.key}
                      collapsed={railCollapsed}
                      onClick={() => setView(item.key)}
                    />
                  ))}

                  <span
                    className={cn(
                      'mt-1.5 flex items-center gap-1 rounded-md px-1.5 py-1 text-[9.5px] font-medium tracking-wider uppercase opacity-45',
                      railCollapsed && 'justify-center px-0',
                    )}
                  >
                    <ChevronDown className="size-2.5" />
                    {railCollapsed ? null : 'Setup'}
                  </span>
                  {railSetupItems.map(item => (
                    <RailItem
                      key={item.key}
                      label={item.label}
                      icon={item.icon}
                      active={view === item.key}
                      collapsed={railCollapsed}
                      onClick={() => setView(item.key)}
                    />
                  ))}
                  </>
                )}
              </nav>

              <div className="mt-auto flex flex-col gap-0.5">
                <div
                  className={cn(
                    'flex items-center gap-1.5 px-1.5 py-1',
                    railCollapsed && 'justify-center px-0',
                  )}
                >
                  {railCollapsed ? null : (
                    <>
                      <img
                        src="/soku-logo-square.png"
                        alt=""
                        className="size-4 shrink-0"
                      />
                      <span className="text-[12px] font-medium">Soku</span>
                      <span className="rounded bg-white/12 px-1 py-px text-[8.5px] font-semibold tracking-wide">
                        PRO
                      </span>
                    </>
                  )}
                  <span
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full bg-orange-500 text-[9.5px] font-semibold text-white',
                      !railCollapsed && 'ml-auto',
                    )}
                  >
                    W
                  </span>
                </div>
              </div>
            </aside>
          )}

          {/* The content card — `SidebarInset md:m-2 md:rounded-xl` floating on the
          frame. On the Chat surface the history panel and canvas share this one
          surface, with the horizon shader spanning the whole card behind both
          (the panel is translucent over it, exactly like the real new-chat
          skin). Every other surface is its own real page replica. */}
          <div className="text-foreground relative isolate m-1.5 flex min-w-0 flex-1 overflow-hidden rounded-[10px] bg-[#FBF5EA] shadow-sm">
            {view === 'chat' ? <ChatView /> : null}
            {view === 'dashboard' ? <DashboardDemo /> : null}
            {view === 'files' ? <FilesDemo /> : null}
            {view === 'creatives' ? (
              <CreativeScenario onCanvasChange={setCanvasOpen} />
            ) : null}
            {view === 'automations' ? <WorkspaceAutomations /> : null}
            {isOrgView(view) ? (
              <WorkspaceOrganization
                section={
                  view === 'org-overview'
                    ? 'overview'
                    : view === 'org-chats'
                      ? 'chats'
                      : 'activity'
                }
              />
            ) : null}
            {view === 'integrations' ? <IntegrationsDemo /> : null}
            {view === 'skills' ? <SkillsDemo /> : null}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Chat (the new-chat home screen) ─────────────────────────────────── */

function ChatView() {
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeChat, setActiveChat] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [composerText, setComposerText] = useState('')

  // In the product the horizon shader is mounted by `/chat/new` only
  // (`new-chat-horizon-shader.tsx` returns null on any other path), and the
  // history panel drops its glass skin for `bg-sidebar/40` the moment a
  // conversation is open. The demo follows the same switch, so an open
  // transcript here looks like an open transcript in the app.
  const openChat = activeChat ? demoTranscripts[activeChat] : undefined

  const query = searchQuery.trim().toLowerCase()
  const filteredChats = Object.entries(conversationGroups)
    .map(([group, items]) => ({
      group,
      items: query
        ? items.filter(item => item.toLowerCase().includes(query))
        : items,
    }))
    .filter(entry => entry.items.length > 0)

  return (
    <>
      {/* Ground. New chat = the horizon shader, verbatim from
          `new-chat-horizon-shader.tsx`; an open conversation = the app's plain
          `--background`, because the product unmounts the shader off
          `/chat/new`. */}
      {openChat ? (
        <div aria-hidden className="absolute inset-0 bg-white" />
      ) : (
        <div
          aria-hidden
          className="absolute inset-0 opacity-55"
          style={{
            backgroundImage: [
              'radial-gradient(82% 68% at 8% 14%, rgba(215, 224, 234, 0.95) 0%, transparent 72%)',
              'radial-gradient(76% 70% at 88% 18%, rgba(233, 161, 93, 0.88) 0%, transparent 70%)',
              'radial-gradient(88% 76% at 50% 66%, rgba(245, 201, 142, 0.84) 0%, transparent 74%)',
              'linear-gradient(180deg, #FFF1D8 0%, #FBF5EA 100%)',
            ].join(', '),
          }}
        />
      )}

      {/* Chat history — `chat-history-panel.tsx` at 0.8: 240px → 204px, 36px →
          28px rows. Two skins, like the real panel: translucent glass over the
          shader on new chat, flat `bg-sidebar/40` once a conversation is open.
          rounded-l matches the card's own radius: the backdrop-filter here
          escapes the parent's overflow-hidden rounded clip in Chrome, so the
          panel must round its own left corners. */}
      <aside
        className={cn(
          'relative hidden w-[204px] shrink-0 flex-col rounded-l-[10px] border-r lg:flex',
          openChat
            ? 'border-black/[0.08] bg-[#f3f3f4]/40'
            : 'border-white/55 bg-white/38 shadow-[inset_-1px_0_0_rgba(255,255,255,0.38)] backdrop-blur-xl',
        )}
      >
        {searchOpen ? (
          <div className="flex shrink-0 items-center gap-1 px-1.5 py-1.5">
            <span className="flex h-6.5 flex-1 items-center gap-1.5 rounded-md border border-black/[0.08] bg-white/80 px-1.5">
              <Search className="size-3 shrink-0 text-neutral-400" />
              <input
                autoFocus
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search chats…"
                className="w-full min-w-0 bg-transparent text-[11.5px] text-neutral-700 placeholder:text-neutral-400 focus:outline-none"
              />
            </span>
            <button
              type="button"
              aria-label="Close search"
              onClick={() => {
                setSearchOpen(false)
                setSearchQuery('')
              }}
              className="flex size-5.5 shrink-0 cursor-pointer items-center justify-center rounded-md text-neutral-400 hover:bg-black/[0.05] hover:text-neutral-600"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-1 px-1.5 py-1.5">
            <button
              type="button"
              onClick={() => {
                setActiveChat(null)
                setActiveCategory(null)
                setComposerText('')
              }}
              className="flex flex-1 cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] font-medium text-neutral-800 transition-colors hover:bg-black/[0.05]"
            >
              <SquarePen className="size-3.5" />
              New chat
            </button>
            <button
              type="button"
              aria-label="Search chats"
              onClick={() => setSearchOpen(true)}
              className="flex size-5.5 cursor-pointer items-center justify-center rounded-md text-neutral-400 hover:bg-black/[0.05] hover:text-neutral-600"
            >
              <Search className="size-3.5" />
            </button>
          </div>
        )}

        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto px-1.5">
          {filteredChats.length === 0 ? (
            <p className="px-2 py-3 text-center text-[10.5px] text-neutral-400">
              No chats match “{searchQuery.trim()}”
            </p>
          ) : (
            filteredChats.map(({ group, items }) => (
              <div key={group}>
                <p className="px-1.5 py-1 text-[9.5px] font-medium tracking-wide text-neutral-400 uppercase">
                  {group}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {items.map(item => (
                    <li key={item}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveChat(item)
                          setActiveCategory(null)
                        }}
                        className={cn(
                          'flex h-7 w-full cursor-pointer items-center rounded-md px-2 text-left text-[12px] transition-colors',
                          item === activeChat
                            ? 'bg-black/[0.06] font-medium text-neutral-800'
                            : 'text-neutral-500 hover:bg-black/[0.04]',
                        )}
                      >
                        <span className="truncate" title={item}>
                          {item}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Canvas — `chat/new/page.tsx` at 0.8: max-w-4xl column → 760px,
          left-aligned 40px serif greeting → 36px. */}
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden px-6 sm:px-12">
        <div className="relative z-10 pt-3 lg:hidden">
          <select
            aria-label="Chat examples"
            value={activeChat ?? ''}
            onChange={event => {
              setActiveChat(event.target.value || null)
              setActiveCategory(null)
            }}
            className="w-full rounded-lg border border-black/10 bg-white/70 px-2 py-1.5 text-xs text-neutral-700"
          >
            <option value="">New chat</option>
            {conversationGroups.Examples.map(title => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </select>
        </div>
        {activeChat && openChat ? (
          <TranscriptPane
            key={activeChat}
            title={activeChat}
            entries={openChat}
          />
        ) : (
          // Top-anchored with generous padding (not vertically centered), like
          // the product's clamp-padded landing — the greeting sits lower and
          // the second card row may crop at the frame edge.
          <div className="mx-auto flex w-full max-w-[760px] flex-col gap-5 pt-12 pb-8 sm:gap-6 sm:pt-24">
            <p className="font-serif text-[24px] leading-[32px] tracking-tight text-[#17171a] sm:text-[36px] sm:leading-[47px]">
              Hi, Sarah{' '}
              <span className="inline-block origin-[70%_80%]">👋</span>
              <br />
              What can I do for your <span className="italic">marketing?</span>
            </p>

            <Composer value={composerText} onChange={setComposerText} />

            {/* The nine jobs, in the product's own words — the
                `suggestion-cards.tsx` grid at 0.8. Clicking a card expands the
                category's real prompts over the grid; clicking a prompt fills
                the composer, exactly like the product. */}
            <div className="relative">
              <div
                className={cn(
                  'grid grid-cols-3 gap-2 transition-opacity sm:grid-cols-5 sm:gap-3',
                  activeCategory && 'pointer-events-none opacity-0',
                )}
                aria-hidden={Boolean(activeCategory)}
              >
                {categories.map((category, index) => (
                  <button
                    key={category.title}
                    type="button"
                    onClick={() => setActiveCategory(category.title)}
                    className={cn(
                      'flex cursor-pointer flex-col rounded-[11px] border border-white bg-white/70 p-1.5 text-left shadow-[0px_1px_4px_rgba(0,0,0,0.04)] backdrop-blur-xl transition-colors hover:bg-white/95',
                      // Mobile shows a single 3-card row; the rest return at sm.
                      index >= 3 && 'hidden sm:flex',
                    )}
                  >
                    <span className="block w-full overflow-hidden rounded-lg">
                      <img
                        src={category.image}
                        alt=""
                        className="aspect-[4/3] w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                    <p className="mt-1.5 truncate px-0.5 text-[11.5px] leading-4.5 font-medium text-[#0A0A0A]">
                      {category.title}
                    </p>
                    <p className="mb-0.5 px-0.5 text-[10.5px] leading-[14px] text-[#737373]">
                      {category.description}
                    </p>
                  </button>
                ))}
              </div>

              {/* Expanded category panel — `suggestion-cards.tsx`'s overlay:
                  covers the grid, lists the category's real prompts. */}
              {activeCategory ? (
                <div className="animate-in fade-in-0 zoom-in-[0.98] absolute inset-0 z-10 flex origin-top flex-col overflow-hidden rounded-2xl border border-white bg-white/85 shadow-[0px_2px_10px_rgba(0,0,0,0.06)] backdrop-blur-2xl duration-200 ease-out">
                  <div className="flex shrink-0 items-center gap-2.5 border-b border-[#EFEBE6] px-3 py-2">
                    <img
                      src={
                        categories.find(c => c.title === activeCategory)?.image
                      }
                      alt=""
                      className="size-7 shrink-0 rounded-lg object-cover"
                    />
                    <span className="flex-1 truncate text-[12px] font-semibold text-[#0A0A0A]">
                      {activeCategory}
                    </span>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={() => setActiveCategory(null)}
                      className="flex size-6 cursor-pointer items-center justify-center rounded-md text-neutral-400 hover:bg-black/[0.05] hover:text-neutral-600"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                  <div className="grid flex-1 auto-rows-min gap-0.5 overflow-y-auto p-1.5 sm:grid-cols-2">
                    {(suggestionPrompts[activeCategory] ?? []).map(
                      suggestion => (
                        <button
                          key={suggestion.title}
                          type="button"
                          onClick={() => {
                            setComposerText(suggestion.prompt)
                            setActiveCategory(null)
                          }}
                          className="group flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-left transition-colors hover:bg-black/[0.04]"
                        >
                          <span className="flex-1 truncate text-[11.5px] text-[#0A0A0A]">
                            {suggestion.title}
                          </span>
                          <ArrowUpLeft className="size-3.5 shrink-0 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100" />
                        </button>
                      ),
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/** The composer — `chat-input.tsx`: rounded-[32px] → 28px. Two skins, like
    the real one: the `glow` white/65 glass card on new chat, solid white with
    a soft floating shadow inside a conversation.
 *
 * It really types. The demo cannot run a turn, so sending — Enter or the send
 * button — goes to sign-up with the same event the other demo CTAs fire; but
 * a visitor who clicks the input gets a caret and their own text, instead of
 * the dead paragraph this used to be. */
function Composer({
  value,
  onChange,
  glow = true,
}: {
  value?: string
  onChange?: (next: string) => void
  glow?: boolean
}) {
  const router = useRouter()
  // Uncontrolled unless the caller owns the text (the new-chat composer does —
  // its suggestion panel writes into it).
  const [ownText, setOwnText] = useState('')
  const text = onChange ? (value ?? '') : ownText
  const setText = onChange ?? setOwnText
  const cta = demoCta('Send message')

  function send() {
    track(cta.event, cta.eventProperties)
    router.push(cta.href)
  }

  return (
    <div
      className={cn(
        'rounded-[28px] border',
        glow
          ? 'border-white bg-white/65 shadow-[0px_2px_10px_rgba(0,0,0,0.06)] backdrop-blur-xl'
          : 'border-black/5 bg-white shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]',
      )}
    >
      <textarea
        value={text}
        onChange={event => setText(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            send()
          }
        }}
        rows={text.length > 64 ? 2 : 1}
        placeholder="Type your message here… (type / for commands)"
        aria-label="Message Soku"
        className="block w-full resize-none bg-transparent px-4.5 pt-3.5 text-[14px] leading-5 text-neutral-800 placeholder:text-stone-400 focus:outline-none"
      />
      <div className="flex items-center gap-1 px-3 pt-2 pb-2.5">
        {(
          [
            { icon: Paperclip, label: 'Attach a file' },
            { icon: Command, label: 'Slash commands' },
            { icon: AtSign, label: 'Mention a skill' },
          ] as const
        ).map(({ icon: Icon, label }) => (
          <TrackedCtaLink
            key={label}
            {...demoCta(label)}
            aria-label={`${label} — sign up`}
            title={label}
            className="flex size-6.5 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-black/[0.05] hover:text-stone-600"
          >
            <Icon className="size-4" />
          </TrackedCtaLink>
        ))}
        <span className="ml-auto flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-stone-500">
          Pro
          <ChevronDown className="size-3" />
        </span>
        <TrackedCtaLink
          {...demoCta('Send message')}
          aria-label="Send message — sign up"
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-orange-500 transition hover:opacity-85"
        >
          <ArrowUp className="size-4 text-white" />
        </TrackedCtaLink>
      </div>
    </div>
  )
}

/** A real (desensitized) demo-org conversation, scrollable, with report
    artifacts and the batch-approval card the product shows for write ops. */
function TranscriptPane({
  title,
  entries,
}: {
  title: string
  entries: TranscriptEntry[]
}) {
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      {/* `ChatConversationSurface`'s header: owner avatar + title on the LEFT,
          not a centered caption. */}
      <div className="flex shrink-0 items-center gap-1.5 py-2.5">
        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-orange-500 text-[9px] font-semibold text-white">
          W
        </span>
        <p className="min-w-0 truncate text-[11.5px] font-medium text-neutral-800">
          {title}
        </p>
        <span className="ml-auto hidden shrink-0 text-[9px] text-neutral-400 sm:block">
          {conversationDates[title]
            ? `Historical run · ${conversationDates[title]}`
            : 'Demo Agency · Soku'}
        </span>
      </div>

      <div className="mx-auto flex min-h-0 w-full max-w-[760px] flex-1 flex-col gap-4 overflow-y-auto pr-1 pb-2 [scrollbar-width:thin]">
        {entries.map((entry, index) =>
          entry.role === 'user' ? (
            // `MessageContent` for a user turn: `bg-secondary rounded-lg px-4
            // py-3`, right-aligned, capped at 95% — no glass bubble, no tail.
            <div
              key={index}
              className="ml-auto w-fit max-w-[95%] rounded-lg bg-[oklch(0.97_0_0)] px-3.5 py-2.5 text-[12px] leading-[18px] text-neutral-800"
            >
              {entry.text}
            </div>
          ) : (
            <div
              key={index}
              className="flex w-full min-w-0 flex-col gap-2 self-start"
            >
              <p className="text-[12px] leading-[19px] text-neutral-700">
                {entry.text}
              </p>
              {entry.attachments?.length ? (
                <div
                  className={cn(
                    'mt-2 grid w-full gap-2',
                    entry.attachments.length > 1
                      ? 'grid-cols-2 xl:grid-cols-3'
                      : 'max-w-[280px] grid-cols-1',
                  )}
                >
                  {entry.attachments.map(attachment => (
                    <AgentArtifactCard
                      key={attachment.id}
                      attachment={attachment}
                      readOnly
                      className={cn(
                        'max-w-none min-w-0',
                        attachment.name.startsWith('birch-') &&
                          '[&_img]:object-contain',
                      )}
                    />
                  ))}
                </div>
              ) : null}
              {entry.batch ? <BatchReviewCardDemo batch={entry.batch} /> : null}
            </div>
          ),
        )}
      </div>
      <div className="mx-auto w-full max-w-[760px] shrink-0 py-3">
        <Composer glow={false} />
      </div>
    </div>
  )
}

/**
 * The pending HITL card — `batch-review-card.tsx` on `HitlCardShell` at 0.8:
 * amber shield + summary title, the operation count as the subtitle, the
 * operations list behind the same show/hide toggle (each row expandable to its
 * human summary), and the decision footer OUTSIDE the body with the
 * agent-paused hint on the left and Reject / Approve-all on the right.
 *
 * Deciding is the one thing the demo cannot fake — both buttons are the
 * conversion moment, so they go to sign-up like every other write affordance
 * here. Everything else (expand, collapse, per-op detail) really works.
 */
function BatchReviewCardDemo({
  batch,
}: {
  batch: { summary: string; ops: { action: string; summary: string }[] }
}) {
  // Pending batches open expanded in the product.
  const [expanded, setExpanded] = useState(true)

  return (
    <div className="w-full max-w-[95%] overflow-hidden rounded-xl border border-black/[0.1] bg-white">
      <div className="flex items-start gap-1.5 px-3 pt-3">
        <ShieldAlert className="mt-px size-3.5 shrink-0 text-amber-600" />
        <div className="min-w-0 space-y-0.5">
          <p className="text-[11.5px] leading-snug font-medium text-neutral-900">
            {batch.summary}
          </p>
          <p className="text-[9px] text-neutral-400">
            {batch.ops.length} operations
          </p>
        </div>
      </div>

      <div className="space-y-2 px-3 py-2.5">
        <button
          type="button"
          onClick={() => setExpanded(open => !open)}
          aria-expanded={expanded}
          className="flex cursor-pointer items-center gap-1 text-[10px] text-neutral-500 transition-colors hover:text-neutral-800"
        >
          {expanded ? (
            <ChevronDown className="size-3" />
          ) : (
            <ChevronRight className="size-3" />
          )}
          {expanded
            ? `Hide operations (${batch.ops.length})`
            : `Show all ${batch.ops.length} operations`}
        </button>

        {expanded ? (
          <ol className="space-y-1.5">
            {batch.ops.map((op, index) => (
              <li key={op.summary}>
                <BatchOperationRowDemo index={index + 1} op={op} />
              </li>
            ))}
          </ol>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-black/[0.08] px-3 py-2.5">
        <p className="min-w-0 text-[10px] text-neutral-500">
          Agent is paused — reply below to ask, or decide here.
        </p>
        <div className="flex items-center gap-1.5">
          <TrackedCtaLink
            {...demoCta('Reject batch')}
            className="flex h-6.5 items-center gap-1 rounded-md border border-black/[0.1] bg-white px-2 text-[10.5px] font-medium text-neutral-700 shadow-[0px_1px_2px_rgba(0,0,0,0.03)] transition-colors hover:bg-neutral-50"
          >
            <X className="size-3" />
            Reject batch
          </TrackedCtaLink>
          <TrackedCtaLink
            {...demoCta('Approve batch')}
            className="flex h-6.5 items-center gap-1 rounded-md bg-[#171717] px-2 text-[10.5px] font-medium text-white transition hover:opacity-90"
          >
            <Check className="size-3" />
            Approve all {batch.ops.length}
          </TrackedCtaLink>
        </div>
      </div>
    </div>
  )
}

/** One operation inside the batch card — the product's row: index, wire
    action, status, and the human summary behind the chevron. */
function BatchOperationRowDemo({
  index,
  op,
}: {
  index: number
  op: { action: string; summary: string }
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-lg border border-black/[0.08] bg-white">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-neutral-50"
      >
        <span className="w-4 shrink-0 text-center font-mono text-[9.5px] text-neutral-400">
          {index}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-neutral-800">
          {op.action}
        </span>
        <span className="shrink-0 text-[8.5px] font-medium tracking-wide text-neutral-400 uppercase">
          pending
        </span>
        {open ? (
          <ChevronDown className="size-3 shrink-0 text-neutral-400" />
        ) : (
          <ChevronRight className="size-3 shrink-0 text-neutral-400" />
        )}
      </button>
      {open ? (
        <p className="border-t border-black/[0.06] px-2.5 py-2 text-[10px] leading-[15px] text-neutral-600">
          {op.summary}
        </p>
      ) : null}
    </div>
  )
}

/* ── Shared page chrome ──────────────────────────────────────────────── */

/** `library-header.tsx` at 0.8: white band, border-b, small medium title. */
function LibraryHeader({
  title,
  status,
  children,
}: {
  title: string
  status?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="flex h-10 shrink-0 items-center gap-3 border-b border-black/[0.07] bg-white px-4">
      <p className="shrink-0 text-[12.5px] font-medium text-[#171717]">
        {title}
      </p>
      {status}
      <div className="ml-auto flex items-center gap-1.5">{children}</div>
    </div>
  )
}

/** The app's h-8 header buttons — outline neutral or solid near-black.
    Pass `cta` to make it a sign-up conversion link. */
function HeaderBtn({
  icon: Icon,
  solid = false,
  cta,
  children,
}: {
  icon?: ComponentType<{ className?: string }>
  solid?: boolean
  cta?: string
  children: ReactNode
}) {
  const className = cn(
    'flex h-6.5 items-center gap-1.5 rounded-md px-2 text-[10.5px] font-medium',
    solid
      ? 'bg-[#171717] text-white'
      : 'border border-black/[0.1] bg-white text-neutral-700 shadow-[0px_1px_2px_rgba(0,0,0,0.03)]',
    cta && 'transition hover:opacity-85',
  )
  if (cta) {
    return (
      <TrackedCtaLink {...demoCta(cta)} className={className}>
        {Icon ? <Icon className="size-3" /> : null}
        {children}
      </TrackedCtaLink>
    )
  }
  return (
    <span className={className}>
      {Icon ? <Icon className="size-3" /> : null}
      {children}
    </span>
  )
}

/* ── Dashboard — traced from `(sidebar)/dashboard/` ──────────────────── */

function Sparkline({ points, color }: { points: number[]; color: string }) {
  const max = Math.max(...points)
  const min = Math.min(...points)
  const range = max - min || 1
  const step = 100 / (points.length - 1)
  const coords = points.map(
    (p, i) => `${i * step},${30 - ((p - min) / range) * 26 - 2}`,
  )
  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      className="h-7 w-16 shrink-0"
      aria-hidden
    >
      <polygon
        points={`0,30 ${coords.join(' ')} 100,30`}
        fill={color}
        opacity="0.13"
      />
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function DeltaChip({ delta, good }: { delta: string; good: boolean }) {
  const Arrow = delta.startsWith('−') ? ArrowDownRight : ArrowUpRight
  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-tight',
        good
          ? 'bg-emerald-500/12 text-emerald-600'
          : 'bg-red-500/10 text-red-600',
      )}
    >
      <Arrow className="size-2.5" />
      {delta.replace('−', '')}
    </span>
  )
}

function MetricTile({
  icon: Icon,
  label,
  value,
  delta,
  good,
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  value: string
  delta: string
  good: boolean
}) {
  const Trend = delta.startsWith('−') ? TrendingDown : TrendingUp
  return (
    <div className="flex min-w-0 flex-col justify-between gap-1.5 rounded-lg border border-black/[0.06] bg-neutral-50 px-2.5 py-2">
      <div className="flex items-center gap-1.5">
        <Icon className="size-3 shrink-0 text-neutral-400" />
        <span className="min-w-0 flex-1 truncate text-[10px] tracking-tight text-neutral-500">
          {label}
        </span>
        <span
          className={cn(
            'flex shrink-0 items-center gap-0.5 font-mono text-[9.5px] font-semibold tracking-tight',
            good ? 'text-emerald-600' : 'text-red-600',
          )}
        >
          <Trend className="size-2.5" />
          {delta}
        </span>
      </div>
      <span className="truncate font-mono text-[14px] leading-tight font-medium tracking-tight text-[#171717]">
        {value}
      </span>
    </div>
  )
}

// 14 daily points per series — "All conversions" black, Purchase blue,
// Lead emerald, matching the real group palette.
const TREND = {
  all: [58, 62, 57, 67, 64, 72, 69, 78, 74, 83, 79, 88, 86, 93],
  purchase: [36, 40, 37, 43, 41, 47, 45, 51, 48, 55, 52, 58, 57, 62],
  lead: [20, 22, 19, 24, 22, 26, 23, 27, 25, 29, 26, 31, 29, 32],
}

function trendPath(points: number[]): string {
  const max = 100
  const step = 300 / (points.length - 1)
  return points.map((p, i) => `${i * step},${96 - (p / max) * 88}`).join(' ')
}

function DashboardDemo() {
  const kpis = [
    {
      icon: DollarSign,
      label: 'Cost',
      value: '12.4K',
      delta: '8.2%',
      good: false,
      spark: TREND.all,
      color: '#3b82f6',
    },
    {
      icon: BarChart4,
      label: 'All Conversions',
      value: '1,284',
      delta: '12.4%',
      good: true,
      spark: TREND.purchase,
      color: '#059669',
    },
    {
      icon: Banknote,
      label: 'All Conv. Value',
      value: '48.6K',
      delta: '9.1%',
      good: true,
      spark: TREND.all,
      color: '#059669',
    },
    {
      icon: Target,
      label: 'All ROAS',
      value: '3.92x',
      delta: '−2.3%',
      good: false,
      spark: TREND.lead,
      color: '#dc2626',
    },
  ]
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
      <LibraryHeader
        title="Dashboard"
        status={
          <span className="flex items-center gap-1.5 text-[10px] text-neutral-400">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            Synced 2 hours ago
          </span>
        }
      >
        <HeaderBtn icon={Target}>Conversion groups</HeaderBtn>
        <HeaderBtn>Sync</HeaderBtn>
        <HeaderBtn icon={Download}>Export</HeaderBtn>
      </LibraryHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-neutral-50/70 px-4 py-3">
        {/* View tabs + filters — `view-tabs.tsx` / `filter-bar.tsx`. */}
        <div className="flex shrink-0 flex-wrap items-end justify-between gap-x-4 border-b border-black/[0.07]">
          <div className="flex items-center">
            {['Overview', 'Breakdown', 'Quadrants', 'GA4 Analytics'].map(
              (tab, index) => (
                <span
                  key={tab}
                  className={cn(
                    'relative px-2.5 py-2 text-[11.5px] leading-tight font-medium',
                    index === 0 ? 'text-[#171717]' : 'text-neutral-400',
                  )}
                >
                  {tab}
                  {index === 0 ? (
                    <span className="absolute right-0 -bottom-px left-0 h-0.5 rounded-t-sm bg-[#171717]" />
                  ) : null}
                </span>
              ),
            )}
          </div>
          <div className="flex items-center gap-1.5 pb-1.5">
            <span className="flex h-6.5 items-center gap-1.5 rounded-md border border-black/[0.1] bg-white px-2 text-[10.5px] text-neutral-600">
              All accounts
              <ChevronsUpDown className="size-3 opacity-50" />
            </span>
            <span className="flex h-6.5 items-center gap-1.5 rounded-md border border-black/[0.1] bg-white px-2 text-[10.5px] text-neutral-600">
              <Calendar className="size-3 text-neutral-400" />
              Aug 4 – Aug 17
              <span className="rounded-md bg-neutral-100 px-1 py-0.5 text-[9px] text-neutral-500">
                14 days
              </span>
            </span>
          </div>
        </div>

        {/* KPI strip — `kpi-strip.tsx`: mono numerals + area sparklines. */}
        <div className="grid shrink-0 grid-cols-4 gap-2.5">
          {kpis.map(kpi => (
            <div
              key={kpi.label}
              className="flex min-w-0 flex-col justify-between gap-2 rounded-xl border border-black/[0.06] bg-white p-3 shadow-[0px_1px_3px_rgba(0,0,0,0.04)]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5 text-[10.5px] font-medium text-neutral-500">
                  <kpi.icon className="size-3 shrink-0" />
                  <span className="truncate">{kpi.label}</span>
                </span>
                <DeltaChip delta={kpi.delta} good={kpi.good} />
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="truncate font-mono text-[19px] leading-none font-medium tracking-tight text-[#171717]">
                  {kpi.value}
                </span>
                <Sparkline points={kpi.spark} color={kpi.color} />
              </div>
            </div>
          ))}
        </div>

        {/* Summary tiles — `summary-cards.tsx` "Account & conversion groups". */}
        <div className="shrink-0 overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2.5 border-b border-black/[0.07] px-3.5 py-2">
            <p className="text-[11.5px] font-medium tracking-tight text-[#171717]">
              Account & conversion groups
            </p>
            <p className="truncate text-[9.5px] text-neutral-400">
              Brand totals and your P0 conversion groups · vs Jul 21 – Aug 3
            </p>
          </div>
          <div className="grid grid-cols-5 gap-2 p-3">
            <MetricTile
              icon={DollarSign}
              label="Cost"
              value="12.4K"
              delta="+8%"
              good={false}
            />
            <MetricTile
              icon={MousePointerClick}
              label="Clicks"
              value="48.2K"
              delta="+11%"
              good
            />
            <MetricTile
              icon={Eye}
              label="Impressions"
              value="1.9M"
              delta="+6%"
              good
            />
            <MetricTile
              icon={Coins}
              label="CPM"
              value="6.52"
              delta="−4%"
              good
            />
            <MetricTile
              icon={BarChart4}
              label="ROAS"
              value="3.92x"
              delta="+3%"
              good
            />
          </div>
        </div>

        {/* Trend chart — `trend-chart.tsx`: line chart by conversion group. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex shrink-0 items-center gap-2.5 border-b border-black/[0.07] px-3.5 py-2">
            <p className="text-[11.5px] font-medium tracking-tight text-[#171717]">
              Trend by conversion group
            </p>
            <p className="truncate text-[9.5px] text-neutral-400">
              Daily series · pick a metric to compare groups
            </p>
            <span className="ml-auto flex h-6 items-center gap-1.5 rounded-md border border-black/[0.1] bg-white px-2 text-[10px] text-neutral-600">
              Conversions
              <ChevronDown className="size-2.5 opacity-50" />
            </span>
          </div>
          <div className="flex min-h-0 flex-1 flex-col px-3.5 pt-2 pb-2">
            <div className="mb-1.5 flex shrink-0 flex-wrap items-center gap-3">
              {[
                { label: 'All conversions', color: '#171717' },
                { label: 'Purchase', color: '#3b82f6' },
                { label: 'Lead', color: '#10b981' },
              ].map(series => (
                <span
                  key={series.label}
                  className="flex items-center gap-1.5 text-[10px] text-neutral-500"
                >
                  <span
                    className="size-2 rounded-[3px]"
                    style={{ backgroundColor: series.color }}
                  />
                  {series.label}
                </span>
              ))}
            </div>
            <svg
              viewBox="0 0 300 100"
              preserveAspectRatio="none"
              className="min-h-0 w-full flex-1"
              aria-hidden
            >
              {[24, 48, 72].map(y => (
                <line
                  key={y}
                  x1="0"
                  y1={y}
                  x2="300"
                  y2={y}
                  stroke="#e5e5e5"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              <polyline
                points={trendPath(TREND.all)}
                fill="none"
                stroke="#171717"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
              <polyline
                points={trendPath(TREND.purchase)}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
              <polyline
                points={trendPath(TREND.lead)}
                fill="none"
                stroke="#10b981"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div className="flex shrink-0 justify-between pt-1 font-mono text-[9px] tracking-tight text-neutral-400">
              {[
                '08/04',
                '08/06',
                '08/08',
                '08/10',
                '08/12',
                '08/14',
                '08/17',
              ].map(date => (
                <span key={date}>{date}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Files — traced from `(sidebar)/files/` ──────────────────────────── */

function DocTile({
  name,
  ext,
  dot,
  selected = false,
}: {
  name: string
  ext: string
  dot: string
  selected?: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg p-1.5 text-center',
        selected && 'bg-black/[0.08]',
      )}
    >
      <span className="flex h-[70px] items-center justify-center">
        <span className="relative flex h-[70px] w-[53px] flex-col gap-[4px] rounded-md border border-black/[0.08] bg-white px-2 py-2 shadow-sm">
          {['w-4/5', 'w-3/5', 'w-4/5', 'w-2/5', 'w-3/5'].map((w, i) => (
            <span
              key={i}
              className={cn('h-[2.5px] rounded-full bg-neutral-300/70', w)}
            />
          ))}
          <span className="absolute right-1 bottom-1 flex items-center gap-0.5">
            <span className={cn('size-1 rounded-full', dot)} />
            <span className="text-[6.5px] font-semibold tracking-wide text-neutral-500">
              {ext}
            </span>
          </span>
        </span>
      </span>
      <span className="line-clamp-2 w-full text-[10px] leading-tight break-words text-neutral-700">
        {name}
      </span>
    </div>
  )
}

function FilesDemo() {
  const treeFolders = ['brand-assets', 'ugc-batch-july', 'winning-creatives']
  const treeFiles = [
    'brand-style.md',
    'brand-voice.md',
    'competitor-research.md',
  ]
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
      <LibraryHeader title="Files">
        <HeaderBtn icon={FolderPlus}>New folder</HeaderBtn>
        <HeaderBtn icon={FilePlus}>New note</HeaderBtn>
        <HeaderBtn icon={Upload} solid cta="Upload files">
          Upload
        </HeaderBtn>
      </LibraryHeader>

      <div className="flex min-h-0 flex-1">
        {/* Folder tree — 240px → 176px, amber-filled folders. */}
        <aside className="hidden w-[176px] shrink-0 flex-col gap-0.5 overflow-hidden border-r border-black/[0.07] p-1.5 text-[11px] md:flex">
          <span className="flex items-center gap-1.5 rounded-md bg-neutral-100 px-2 py-1 font-medium text-[#171717]">
            <ChevronDown className="size-3 text-neutral-400" />
            Context
          </span>
          {treeFolders.map(folder => (
            <span
              key={folder}
              className="flex items-center gap-1.5 rounded-md py-1 pr-2 pl-3.5 text-neutral-500"
            >
              <ChevronRight className="size-3 shrink-0 text-neutral-300" />
              <Folder
                className="size-3.5 shrink-0 fill-amber-200 text-amber-400"
                strokeWidth={1.5}
              />
              <span className="truncate">{folder}</span>
            </span>
          ))}
          {treeFiles.map(file => (
            <span
              key={file}
              className="flex items-center gap-1.5 rounded-md py-1 pr-2 pl-[30px] text-neutral-500"
            >
              <FileText className="size-3.5 shrink-0" strokeWidth={1.5} />
              <span className="truncate">{file}</span>
            </span>
          ))}
        </aside>

        {/* Tile grid — auto-fill 110px → 88px tiles. */}
        <div className="relative min-w-0 flex-1 overflow-hidden p-3">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2">
            {treeFolders.concat('audience-research').map(folder => (
              <div
                key={folder}
                className="flex flex-col items-center gap-1.5 rounded-lg p-1.5 text-center"
              >
                <span className="flex h-[70px] items-center justify-center">
                  <Folder
                    className="size-13 fill-amber-200 text-amber-400"
                    strokeWidth={1}
                  />
                </span>
                <span className="line-clamp-2 w-full text-[10px] leading-tight text-neutral-700">
                  {folder}
                </span>
              </div>
            ))}

            {/* Image thumbnails — real brand-asset look. */}
            <div className="flex flex-col items-center gap-1.5 rounded-lg bg-black/[0.08] p-1.5 text-center">
              <span className="flex h-[70px] items-center justify-center">
                <img
                  src={`${REMIX_CDN}/ugc-first/v5.2/final-sku-onyx.jpg`}
                  alt=""
                  className="max-h-[70px] max-w-[70px] rounded-md border border-black/[0.08] bg-white object-cover shadow-sm"
                  loading="lazy"
                />
              </span>
              <span className="line-clamp-2 w-full text-[10px] leading-tight text-neutral-700">
                product-hero.jpg
              </span>
            </div>
            <div className="flex flex-col items-center gap-1.5 rounded-lg p-1.5 text-center">
              <span className="flex h-[70px] items-center justify-center">
                <img
                  src={`${REMIX_CDN}/mascot-tryon/spot-poster.jpg`}
                  alt=""
                  className="max-h-[70px] max-w-[70px] rounded-md border border-black/[0.08] bg-white object-cover shadow-sm"
                  loading="lazy"
                />
              </span>
              <span className="line-clamp-2 w-full text-[10px] leading-tight text-neutral-700">
                lifestyle-shot.jpg
              </span>
            </div>

            {/* Video + audio tiles. */}
            <div className="flex flex-col items-center gap-1.5 rounded-lg bg-black/[0.08] p-1.5 text-center">
              <span className="flex h-[70px] items-center justify-center">
                <span className="flex h-[70px] w-[80px] items-center justify-center rounded-md bg-neutral-900 shadow-sm">
                  <PlayCircle
                    className="size-7 text-white/90"
                    strokeWidth={1.5}
                  />
                </span>
              </span>
              <span className="line-clamp-2 w-full text-[10px] leading-tight text-neutral-700">
                founder-story.mp4
              </span>
            </div>
            <div className="flex flex-col items-center gap-1.5 rounded-lg p-1.5 text-center">
              <span className="flex h-[70px] items-center justify-center">
                <span className="flex h-[70px] w-[70px] items-center justify-center rounded-md border border-black/[0.08] bg-amber-50 shadow-sm">
                  <Headphones
                    className="size-7 text-amber-500"
                    strokeWidth={1.5}
                  />
                </span>
              </span>
              <span className="line-clamp-2 w-full text-[10px] leading-tight text-neutral-700">
                voiceover-v2.mp3
              </span>
            </div>

            <DocTile name="brand-style.md" ext="MD" dot="bg-blue-500" />
            <DocTile name="brand-voice.md" ext="MD" dot="bg-blue-500" />
            <DocTile name="pitch-deck.pdf" ext="PDF" dot="bg-red-500" />
            <DocTile name="media-plan.csv" ext="CSV" dot="bg-emerald-500" />
          </div>

          {/* Floating selection bar — `context-selection-bar.tsx`. */}
          <div className="absolute inset-x-0 bottom-2.5 flex justify-center">
            <div className="flex items-center gap-1.5 rounded-full border border-black/[0.08] bg-white/95 py-1 pr-1 pl-2.5 shadow-lg backdrop-blur">
              <span className="text-[10px] font-medium text-neutral-500">
                2 selected
              </span>
              <span className="flex size-5 items-center justify-center rounded-full text-neutral-400">
                <X className="size-3" />
              </span>
              <span className="flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium text-neutral-600">
                <Download className="size-3" />
                Download
              </span>
              <span className="flex items-center gap-1 rounded-full bg-[#171717] px-2.5 py-1 text-[10px] font-medium text-white">
                Chat with Soku
              </span>
            </div>
          </div>
        </div>

        {/* Docked chat panel — `library-chat-panel.tsx`, 400px → 236px. */}
        <aside className="hidden w-[236px] shrink-0 flex-col border-l border-black/[0.07] lg:flex">
          <div className="flex h-10 shrink-0 items-center gap-1 border-b border-black/[0.07] px-2.5">
            <span className="flex-1 text-[11.5px] font-medium text-[#171717]">
              Chat
            </span>
            <SquarePen className="size-3.5 text-neutral-400" />
            <ExternalLink className="size-3.5 text-neutral-400" />
            <PanelRightClose className="size-3.5 text-neutral-400" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 px-4 text-center">
            <img
              src="/soku-logo-square.png"
              alt=""
              className="size-6 opacity-80"
            />
            <p className="text-[10px] leading-4 text-neutral-400">
              Ask about these files — Soku reads them as brand context
            </p>
          </div>
          <div className="shrink-0 p-2">
            <div className="flex items-center gap-1.5 rounded-xl border border-black/[0.1] bg-white px-2.5 py-2 shadow-[0px_1px_3px_rgba(0,0,0,0.04)]">
              <span className="flex-1 truncate text-[10.5px] text-neutral-400">
                Ask anything about this brand…
              </span>
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-orange-500">
                <ArrowUp className="size-3 text-white" />
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

/* ── Integrations / Skills — marketplace-hub pages ───────────────────── */

const ICONS_CDN = 'https://cdn.soku.ai/marketing/integrations/icons'

/** The full native-platform registry, verbatim from `lib/integrations.tsx`
    (names, order, descriptions). Icons come from the same CDN set the app
    uses; the few platforms whose logos are inline SVGs in the app fall back
    to the app's letter-tile treatment. */
type PlatformCard = {
  name: string
  icon?: string
  fallback?: { label: string; className: string }
  status: string
  connected: boolean
  isNew?: boolean
  description: string
}

const platformCards: PlatformCard[] = [
  {
    name: 'ChatGPT Ads',
    icon: `${ICONS_CDN}/openai.svg`,
    status: 'Connected · 1 report account',
    connected: true,
    isNew: true,
    description:
      'Install the Soku Pixel and view ChatGPT Ads report accounts provisioned by Soku support',
  },
  {
    name: 'Meta Ads',
    icon: `${ICONS_CDN}/meta.svg`,
    status: 'Connected · 2 accounts',
    connected: true,
    description:
      'Connect your Meta ads account to get insights and optimize campaigns',
  },
  {
    name: 'Google Ads',
    icon: `${ICONS_CDN}/google.svg`,
    status: 'Connected · 3 accounts',
    connected: true,
    description:
      'Connect your Google ads account to get insights and optimize campaigns',
  },
  {
    name: 'GA4',
    icon: `${ICONS_CDN}/ga4.svg`,
    status: 'Connected',
    connected: true,
    description:
      'Connect your Google Analytics to track conversions and user behavior',
  },
  {
    name: 'Google Search Console',
    icon: `${ICONS_CDN}/searchconsole.png`,
    status: 'Connected',
    connected: true,
    description:
      'Connect Google Search Console to analyze organic queries, pages, and site performance',
  },
  {
    name: 'PostHog',
    icon: `${ICONS_CDN}/posthog.png`,
    status: 'Connected',
    connected: true,
    description:
      'Connect PostHog to analyze funnels, retention, and product events',
  },
  {
    name: 'TikTok Ads',
    icon: `${ICONS_CDN}/tiktok.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect your TikTok ads account to get insights and optimize campaigns',
  },
  {
    name: 'LinkedIn Ads',
    icon: `${ICONS_CDN}/linkedin.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect your LinkedIn ad accounts to get insights and manage B2B campaigns',
  },
  {
    name: 'Shopify',
    icon: `${ICONS_CDN}/shopify.svg`,
    status: 'Connected · soku-demo',
    connected: true,
    description:
      'Connect your Shopify store to read products, orders, and customers',
  },
  {
    name: 'Shoplazza',
    fallback: { label: 'S', className: 'bg-red-600 text-white' },
    status: 'Not connected',
    connected: false,
    description:
      'Link the Shoplazza stores installed from the Shoplazza App Store to your brands',
  },
  {
    name: 'SHOPLINE',
    fallback: { label: 'S', className: 'bg-[#0b1c3f] text-white' },
    status: 'Not connected',
    connected: false,
    description:
      'Link the SHOPLINE stores installed from the SHOPLINE App Store to your brands',
  },
  {
    name: 'Stripe',
    icon: `${ICONS_CDN}/stripe.svg`,
    status: 'Connected',
    connected: true,
    description:
      'Connect Stripe to look up payments, customers, and subscriptions',
  },
  {
    name: 'AppsFlyer',
    icon: `${ICONS_CDN}/appsflyer.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect AppsFlyer to read mobile attribution, ROAS, SKAN, and audiences',
  },
  {
    name: 'Adjust',
    icon: `${ICONS_CDN}/adjust.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect Adjust to read mobile attribution, ROAS, SKAN, and retention',
  },
  {
    name: 'Sprout Social',
    icon: `${ICONS_CDN}/sprout-social.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect Sprout Social to read social profile and post analytics',
  },
  {
    name: 'AppLovin Ads',
    fallback: { label: 'A', className: 'bg-neutral-900 text-white' },
    status: 'Not connected',
    connected: false,
    description:
      'Read-only AppLovin campaign, creative and asset performance, spend, cohort ROAS and retention',
  },
  {
    name: 'Toast',
    icon: `${ICONS_CDN}/toast.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect Toast to read restaurant orders, checks, payments and refunds',
  },
  {
    name: 'BigQuery',
    fallback: { label: 'B', className: 'bg-blue-600 text-white' },
    status: 'Connected',
    connected: true,
    description:
      'Connect BigQuery to explore datasets and run reviewed SQL queries',
  },
]

/** The Composio long tail, verbatim from the same registry. */
const allApps: PlatformCard[] = [
  {
    name: 'Notion',
    icon: `${ICONS_CDN}/composio/notion.svg`,
    status: 'Connected',
    connected: true,
    description:
      'Connect your Notion workspace to create pages and query databases',
  },
  {
    name: 'Gmail',
    icon: `${ICONS_CDN}/composio/gmail.svg`,
    status: 'Not connected',
    connected: false,
    description: 'Connect Gmail to read and send messages from your account',
  },
  {
    name: 'Google Docs',
    icon: `${ICONS_CDN}/composio/googledocs.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect Google Docs to create and read documents in your Drive',
  },
  {
    name: 'Google Sheets',
    icon: `${ICONS_CDN}/composio/googlesheets.svg`,
    status: 'Connected',
    connected: true,
    description:
      'Connect Google Sheets to read and write spreadsheets in your Drive',
  },
  {
    name: 'Google Drive',
    icon: `${ICONS_CDN}/composio/googledrive.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect Google Drive to find, read, and manage files in your Drive',
  },
  {
    name: 'Google Calendar',
    icon: `${ICONS_CDN}/composio/googlecalendar.svg`,
    status: 'Not connected',
    connected: false,
    description:
      'Connect Google Calendar to read your schedule and create or update events',
  },
  {
    name: 'Monday.com',
    icon: '/integrations/icons/monday.png',
    status: 'Not connected',
    connected: false,
    description:
      'Connect Monday.com to read boards and items and create or update them',
  },
]

function PlatformCardView({ platform }: { platform: PlatformCard }) {
  return (
    <div className="flex flex-col overflow-hidden rounded-[10px] border border-black/[0.07] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.04)]">
      <div className="flex flex-1 flex-col gap-1.5 px-3.5 py-2.5">
        <div className="flex items-start gap-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center">
            {platform.icon ? (
              <img
                src={platform.icon}
                alt=""
                className="size-6 rounded-md object-contain"
                loading="lazy"
              />
            ) : (
              <span
                className={cn(
                  'flex size-6 items-center justify-center rounded-md text-[11px] font-semibold',
                  platform.fallback?.className,
                )}
              >
                {platform.fallback?.label}
              </span>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[11.5px] font-semibold tracking-tight text-[#171717]">
                {platform.name}
              </span>
              {platform.isNew ? (
                <span className="shrink-0 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[8.5px] leading-none font-medium text-blue-700">
                  New
                </span>
              ) : null}
            </span>
            <span
              className={cn(
                'block truncate text-[9.5px]',
                platform.connected ? 'text-emerald-700' : 'text-neutral-400',
              )}
            >
              {platform.status}
            </span>
          </span>
        </div>
        <p className="line-clamp-2 text-[9.5px] leading-relaxed text-neutral-400">
          {platform.description}
        </p>
      </div>
      <div className="flex items-center gap-1.5 border-t border-black/[0.06] px-3.5 py-1.5">
        {platform.connected ? (
          <span className="flex h-5.5 items-center gap-1 rounded-md border border-black/[0.1] bg-white px-1.5 text-[9.5px] font-medium text-neutral-600">
            Manage
          </span>
        ) : (
          <span className="flex h-5.5 items-center gap-1 rounded-md border border-black/[0.1] bg-white px-1.5 text-[9.5px] font-medium text-neutral-600">
            <PlugZap className="size-2.5" />
            Connect
          </span>
        )}
        {platform.isNew ? (
          <span className="flex h-5.5 items-center gap-1 rounded-md border border-black/[0.1] bg-white px-1.5 text-[9.5px] font-medium text-neutral-600">
            <BookOpen className="size-2.5" />
            Guide
          </span>
        ) : null}
      </div>
    </div>
  )
}

function IntegrationsDemo() {
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-[#f6f5f4]">
      <div className="flex shrink-0 items-start justify-between px-5 pt-4 pb-3">
        <p className="font-serif text-[19px] leading-6 tracking-tight text-[#171717]">
          Integrations
        </p>
        <HeaderBtn icon={Wallet}>Finances</HeaderBtn>
      </div>

      {/* The full registry from `lib/integrations.tsx`, scrollable. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pb-4 [scrollbar-width:thin]">
        {/* Tabs + search — `page-content.tsx`. */}
        <div className="flex shrink-0 items-center gap-2.5">
          <span className="flex items-center gap-0.5 rounded-lg bg-neutral-200/60 p-0.5">
            <span className="rounded-md bg-white px-2.5 py-1 text-[10.5px] font-medium text-[#171717] shadow-sm">
              Bring Your Own
            </span>
            <span className="px-2.5 py-1 text-[10.5px] font-medium text-neutral-500">
              Built-in
            </span>
          </span>
          <span className="flex h-6.5 w-[220px] items-center gap-1.5 rounded-md border border-black/[0.1] bg-white px-2 text-[10.5px] text-neutral-400">
            <Search className="size-3" />
            Search integrations
          </span>
        </div>

        <div className="shrink-0">
          <p className="text-[12.5px] font-semibold text-[#171717]">
            Ad & analytics platforms
          </p>
          <p className="text-[10px] text-neutral-400">
            First-party connections for campaign, analytics, and revenue data.
          </p>
        </div>

        <div className="grid shrink-0 grid-cols-3 gap-2.5">
          {platformCards.map(platform => (
            <PlatformCardView key={platform.name} platform={platform} />
          ))}
        </div>

        <div className="shrink-0 pt-1">
          <p className="text-[12.5px] font-semibold text-[#171717]">All apps</p>
          <p className="text-[10px] text-neutral-400">
            Connect the rest of your stack — email, docs, CRM, and more.
          </p>
        </div>

        <div className="grid shrink-0 grid-cols-3 gap-2.5">
          {allApps.map(app => (
            <PlatformCardView key={app.name} platform={app} />
          ))}
        </div>

        <p className="shrink-0 pt-1 pb-2 text-center text-[10px] text-neutral-400">
          Looking for some other platforms?{' '}
          <span className="underline">ceo@soku.ai</span>
        </p>
      </div>
    </div>
  )
}

const marketplaceSkills: {
  title: string
  slug: string
  category: string
  version: string
  description: string
}[] = [
  {
    title: 'Analyze and Manage Google Ads Campaigns',
    slug: 'google-ads',
    category: 'Growth & Ads',
    version: 'v1.1.0',
    description:
      'Work directly with Google Ads entities, reports, audiences, labels, conversion actions, and account structure when a task needs custom control.',
  },
  {
    title: 'Analyze and Manage Meta Ads Campaigns',
    slug: 'meta-ads',
    category: 'Growth & Ads',
    version: 'v1.2.0',
    description:
      'Work directly with Meta Ads campaigns, audiences, pixels, lead forms, creative assets, rules, and targeting when a task needs custom control.',
  },
  {
    title: 'Analyze Backlinks and Uncover Link Opportunities',
    slug: 'backlink-profiler',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Analyze a domain’s backlink profile, including referring domains, anchor text, link history, competitor links, and backlink gaps.',
  },
  {
    title: 'Analyze Search Trends and Spot Rising Demand',
    slug: 'trend-analyzer',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Analyze keyword trend history, seasonality, regional demand, and SERP stability to guide timing and budget decisions.',
  },
  {
    title: 'Audit ChatGPT Ads and Find Growth Opportunities',
    slug: 'chatgpt-ads-audit',
    category: 'Growth & Ads',
    version: 'v0.1.1',
    description:
      'ChatGPT Ads performance audit — 14-day trend + T-1 snapshot across date, conversion group, campaign, ad unit, landing page, and optional UTM dimension',
  },
  {
    title: 'Audit Search Terms and Cut Wasted Spend',
    slug: 'audit-search-terms',
    category: 'Growth & Ads',
    version: 'v0.6.0',
    description:
      'Review Google Ads search terms to find wasted spend, negative-keyword opportunities, healthy queries, and terms that deserve closer monitoring.',
  },
  {
    title: 'Audit Your Site and Prioritize High-Impact Fixes',
    slug: 'site-audit',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Audit a landing page or full site for technical SEO, page speed, content quality, UX issues, and conversion blockers.',
  },
  {
    title: 'Benchmark Your Brand Across Live AI Answers',
    slug: 'geo-live-benchmark',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Run controlled, repeatable GEO probes across named ChatGPT, Claude, Gemini, and Perplexity models and ChatGPT Search, preserving model, prompt, locali',
  },
  {
    title: 'Break Down Competitor Landing Pages and Offers',
    slug: 'landing-page-spy',
    category: 'Growth & Ads',
    version: 'v0.4.0',
    description:
      'Analyze competitor and owned landing pages to uncover keyword-page strategy, conversion blockers, performance issues, and prioritized fixes.',
  },
  {
    title: 'Build a Data-Backed Paid Media Plan',
    slug: 'ads-media-plan',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Create a structured Google or Meta Ads media plan with campaign structure, keyword or audience strategy, creative direction, and budget assumptions.',
  },
  {
    title: 'Build a Launch-Ready ChatGPT Ads Media Plan',
    slug: 'chatgpt-ads-media-plan',
    category: 'Growth & Ads',
    version: 'v0.3.1',
    description:
      'Turn onboarding notes, landing pages, brand context, and verified use cases into a polished ChatGPT Ads media plan ready for review or handoff.',
  },
  {
    title: 'Create a Weekly ChatGPT Ads Performance Report',
    slug: 'chatgpt-ads-weekly',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'ChatGPT Ads weekly performance report — past 7-day trend + T-1 snapshot across date, conversion group, campaign, ad unit, landing page, and optional U',
  },
  {
    title: 'Deploy Approved Campaigns to ChatGPT Ads',
    slug: 'deploy-chatgpt-ads',
    category: 'Growth & Ads',
    version: 'v0.4.1',
    description:
      'Create and manage paused ChatGPT Ads Campaign -> Ad Group -> Ad hierarchies with live action-schema discovery, HITL, and exact materialization readbac',
  },
  {
    title: 'Deploy Approved Campaigns to Google, Meta, and TikTok Ads',
    slug: 'deploy-ads',
    category: 'Growth & Ads',
    version: 'v0.6.0',
    description:
      'Create, modify, or copy Google, Meta, or TikTok ad campaigns from an approved plan, with user confirmation before changes are applied.',
  },
  {
    title: 'Diagnose Google Ads Quality Score Issues',
    slug: 'audit-quality-score',
    category: 'Growth & Ads',
    version: 'v0.2.0',
    description:
      'Diagnose Google Search Quality Score issues and identify which keywords are raising CPC through weak CTR, ad relevance, or landing-page experience.',
  },
  {
    title: 'Discover High-Intent Keywords Worth Targeting',
    slug: 'keyword-discovery',
    category: 'Growth & Ads',
    version: 'v0.2.0',
    description:
      'Discover and collect keyword opportunities across markets, with intent classification and structured exports for planning or reporting.',
  },
  {
    title: 'Discover High-Value Keywords With Ahrefs',
    slug: 'ahrefs-keyword-discovery',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Discover new keyword opportunities from seed topics, then cluster them by intent and prioritize the ones most likely to drive business value.',
  },
  {
    title: 'Discover the AI Prompts Your Audience Uses',
    slug: 'geo-prompt-research',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Build and maintain an evidence-backed AI-search prompt set for GEO measurement, using modeled conversational demand, customer language, search data, i',
  },
  {
    title: 'Find and Prioritize Your Real Competitors',
    slug: 'find-competitors',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Find and classify competitors for a brand or keyword set, including core rivals, high-budget advertisers, and emerging challengers.',
  },
  {
    title: 'Inspect Google Ads and Diagnose Issues',
    slug: 'ad-inspection-google',
    category: 'Growth & Ads',
    version: 'v0.5.0',
    description:
      'Audit a Google Ads account or investigate sudden performance changes, with prioritized findings on spend waste, delivery issues, search quality, and c',
  },
  {
    title: 'Inspect Meta Ads and Diagnose Issues',
    slug: 'ad-inspection-meta',
    category: 'Growth & Ads',
    version: 'v0.5.0',
    description:
      'Audit a Meta Ads account or investigate performance changes, with findings on creative fatigue, audience saturation, tracking health, and campaign lea',
  },
  {
    title: 'Measure and Improve Visibility in AI Answers',
    slug: 'geo-analysis',
    category: 'Growth & Ads',
    version: 'v0.2.0',
    description:
      'Orchestrate a complete GEO program: research AI-search prompts, measure brand and competitor visibility, run controlled LLM benchmarks, diagnose citat',
  },
  {
    title: 'Monitor Search Ads, Copy, and Competitor Changes',
    slug: 'serp-ads-monitor',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Monitor who is advertising on target keywords, capture live ad copy, and spot new paid-search competitors.',
  },
  {
    title: 'Optimize Content for AI Search Visibility',
    slug: 'geo-content-optimizer',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Turn GEO prompt, mention, retrieval, citation, competitor-page, and live-benchmark evidence into page-specific content briefs, restructuring plans, te',
  },
  {
    title: 'Optimize Google Ads Bids by Device',
    slug: 'optimize-device-bids',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Analyze Google Ads performance by device and recommend bid modifier changes based on CPA or ROAS differences.',
  },
  {
    title: 'Publish and Manage SEO Pages on Your Domain',
    slug: 'seo-hosting',
    category: 'Growth & Ads',
    version: 'v1.2.0',
    description:
      'Primary workflow for first-time SEO/GEO setup and hosted pages.',
  },
  {
    title: 'Review Ads Changes and Find Performance Impacts',
    slug: 'ads-change-history',
    category: 'Growth & Ads',
    version: 'v0.2.0',
    description:
      'Review recent Google Ads account changes so you can see who changed what, when it happened, and which keywords, campaigns, or ad groups were affected.',
  },
  {
    title: 'Spy on App Store Competitors and Growth Tactics',
    slug: 'app-store-spy',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Analyze an iOS or Google Play app with store health, keyword visibility, competitor movement, chart position, and review signals.',
  },
  {
    title: 'Track Ad Optimizations and Measure Their Impact',
    slug: 'optimization-tracker',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Track whether recent ad optimizations improved performance by comparing results against a baseline and flagging anomalies.',
  },
  {
    title: 'Track Brand Mentions and Share of Voice',
    slug: 'mention-tracker',
    category: 'Growth & Ads',
    version: 'v0.2.0',
    description:
      'Monitor brand mentions, citations, retrieval status, share of voice, and trends across AI search surfaces; use for recurring GEO measurement, baseline',
  },
  {
    title: 'Turn Ad Performance Into a Clear Report',
    slug: 'ads-report',
    category: 'Growth & Ads',
    version: 'v0.2.0',
    description:
      'Generate a daily or weekly Google Ads, Meta Ads, or TikTok Ads performance report with KPI trends, breakdowns, status notes, and recommended next acti',
  },
  {
    title: 'Uncover Competitors’ Organic and Paid Keywords',
    slug: 'keyword-spy',
    category: 'Growth & Ads',
    version: 'v0.1.0',
    description:
      'Analyze a competitor domain’s organic and paid keywords, ranking gaps, search opportunities, and budget signals.',
  },
  {
    title: 'Validate Campaigns Before They Go Live',
    slug: 'ads-pre-flight',
    category: 'Growth & Ads',
    version: 'v0.7.0',
    description:
      'Check whether a paid campaign goal is realistic before launch, including budget, CPA target, learning-period requirements, and platform fit.',
  },
  {
    title: 'Build a Conversion-Focused Landing Page',
    slug: 'landing-page-create',
    category: 'Creative & Content',
    version: 'v0.3.0',
    description:
      'Build a production-ready branded landing page from a research brief, using the brand site and competitor examples for design direction.',
  },
  {
    title: 'Design Polished Creative Assets for SaaS',
    slug: 'saas-creative-design',
    category: 'Creative & Content',
    version: 'v0.1.0',
    description:
      'Use a SaaS ad design playbook to shape stronger software and digital-product creatives, including layout, copy, and visual direction.',
  },
  {
    title: 'Find Viral Content Patterns, Hooks, and Creators',
    slug: 'viral-social-intelligence',
    category: 'Creative & Content',
    version: 'v0.1.0',
    description:
      'Analyze competitor videos on YouTube and TikTok to find viral formats, content angles, platform patterns, and creative opportunities.',
  },
  {
    title: 'Generate On-Brand Ad Creative Variations',
    slug: 'generate-ad-creative',
    category: 'Creative & Content',
    version: 'v0.4.0',
    description:
      'Generate branded ad creative variants from a landing page or product brief, using the real brand identity and campaign strategy.',
  },
  {
    title: 'Produce Short-Form Video Ads From a Brief',
    slug: 'video-creative-pipeline',
    category: 'Creative & Content',
    version: 'v0.1.0',
    description:
      'Produce a short-form video ad package from product context, competitor research, script, voiceover, music, captions, and creative direction.',
  },
  {
    title: 'Spy Competitors’ Ad Creatives and Get Insights',
    slug: 'ad-creative-spy',
    category: 'Creative & Content',
    version: 'v0.4.0',
    description:
      'Research competitor ad creatives across global mobile campaigns, Meta, TikTok, and Instagram; use for mobile-app ad discovery, structured platform inv',
  },
  {
    title: 'Turn Long Videos Into Short, Shareable Clips',
    slug: 'clip-video',
    category: 'Creative & Content',
    version: 'v0.1.0',
    description:
      'Turn a long video or URL into short-form highlight clips in multiple aspect ratios for social and paid channels.',
  },
  {
    title: 'Analyze Competitors and Build an Action Plan',
    slug: 'competitive-analysis',
    category: 'Analytics & Research',
    version: 'v0.5.0',
    description:
      'Route competitive research across paid search and a complete GEO workflow, including prompt research, longitudinal AI visibility, controlled LLM bench',
  },
  {
    title: 'Analyze GA4 Traffic, Conversions, and User Behavior',
    slug: 'ga4',
    category: 'Analytics & Research',
    version: 'v0.2.0',
    description:
      'Pull GA4 reporting for traffic, top pages, conversions, events, daily trends, and metric discovery.',
  },
  {
    title: 'Analyze Google Search Console Performance',
    slug: 'gsc',
    category: 'Analytics & Research',
    version: 'v0.1.0',
    description:
      'Pull Google Search Console data: sites, search analytics, top queries and pages, daily trends, sitemaps, and URL inspection.',
  },
  {
    title: 'Analyze Product Growth, Funnels, and Retention',
    slug: 'posthog-growth-analytics',
    category: 'Analytics & Research',
    version: 'v0.1.0',
    description:
      'Analyze PostHog product data to understand funnels, retention, user behavior, campaign quality, and growth opportunities.',
  },
  {
    title: 'Audit Ad Creatives and Find Winning Patterns',
    slug: 'audit-creative',
    category: 'Analytics & Research',
    version: 'v0.2.0',
    description:
      'Review ad creative quality across Google and Meta to find weak assets, fatigue signals, missing variants, and opportunities for stronger creative cove',
  },
  {
    title: 'Audit Conversion Tracking and Fix Measurement Gaps',
    slug: 'audit-tracking',
    category: 'Analytics & Research',
    version: 'v0.2.0',
    description:
      'Audit conversion tracking across Google Ads, Meta Ads, and GA4 to find setup issues, attribution problems, duplicate conversions, and data-quality gap',
  },
  {
    title: 'Compare Google Ads Competitors and Find Gaps',
    slug: 'google-ads-competitive-analysis',
    category: 'Analytics & Research',
    version: 'v0.1.0',
    description:
      'Analyze Google paid search competitors, including keyword gaps, advertisers on the SERP, ad copy themes, landing pages, and media-plan opportunities.',
  },
  {
    title: 'Spy on LinkedIn Competitors and Content Trends',
    slug: 'linkedin-spy',
    category: 'Analytics & Research',
    version: 'v0.1.0',
    description:
      'Analyze a competitor’s LinkedIn content and audience to reveal content strategy, engaged companies, and high-value prospect segments.',
  },
  {
    title: 'Enrich People Records With Useful Contact Data',
    slug: 'person-enrichment',
    category: 'Tools & Operations',
    version: 'v0.1.0',
    description:
      'Turn an email, LinkedIn URL, phone number, or name plus company into a normalized professional profile.',
  },
  {
    title: 'Export Reports to Google Docs or Slides',
    slug: 'export-google-workspace',
    category: 'Tools & Operations',
    version: 'v0.2.0',
    description:
      'Create, read, append, replace, or export content in Google Docs, and export reports or HTML artifacts to Google Slides.',
  },
  {
    title: 'Get Step-by-Step Help Using Soku',
    slug: 'soku-help',
    category: 'Tools & Operations',
    version: 'v1.0.0',
    description:
      'Orientation and onboarding help for using Soku, including applying for or getting started with ChatGPT Ads.',
  },
  {
    title: 'Migrate Your Claude Workspace to Soku',
    slug: 'migrate-from-claude',
    category: 'Tools & Operations',
    version: 'v0.1.0',
    description:
      'Migrate from Claude to Soku: move Claude project files, knowledge docs, and workspace context (brand bibles, meeting notes, brand guidelines) into the',
  },
  {
    title: 'Send and Manage Email From Your Workspace',
    slug: 'agent-email',
    category: 'Tools & Operations',
    version: 'v0.2.0',
    description:
      "Deliver the current conversation's core result and artifacts from the organization's dedicated Agent email address.",
  },
  {
    title: 'Set Up Reusable Tools in Your Agent Workspace',
    slug: 'persistent-tools',
    category: 'Tools & Operations',
    version: 'v0.3.0',
    description:
      'Install command-line tools for the workspace and keep them available after sandbox rebuilds.',
  },
  {
    title: 'Turn Website Visitors Into Company Insights',
    slug: 'ip-enrichment',
    category: 'Tools & Operations',
    version: 'v0.1.0',
    description:
      'Identify the company behind an IP address and return firmographic details such as domain, industry, size, revenue, and location.',
  },
  {
    title: 'Turn Your Workflow Into a Reusable Skill',
    slug: 'skill-creator',
    category: 'Tools & Operations',
    version: 'v0.1.0',
    description:
      'Create a private skill from a team SOP, repeated workflow, or current conversation so the brand can reuse it later.',
  },
]

function SkillsDemo() {
  const [activeGroup, setActiveGroup] = useState('All')
  const groups = [
    'All',
    'Growth & Ads',
    'Creative & Content',
    'Analytics & Research',
    'Tools & Operations',
  ]
  const visibleSkills =
    activeGroup === 'All'
      ? marketplaceSkills
      : marketplaceSkills.filter(skill => skill.category === activeGroup)

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-[#f6f5f4]">
      <div className="shrink-0 px-5 pt-4">
        <p className="font-serif text-[19px] leading-6 tracking-tight text-[#171717]">
          Skill Marketplace
        </p>
        <p className="mt-0.5 text-[10.5px] text-neutral-400">
          Browse and install skills. Installs are scoped to this brand and take
          effect on the next chat turn.
        </p>
      </div>

      {/* Search + upload + category pills — `skills/page.tsx`. The pills
          really filter, like the product. */}
      <div className="flex shrink-0 items-center gap-2 px-5 pt-3">
        <span className="flex h-7 w-[240px] items-center gap-1.5 rounded-md border border-black/[0.1] bg-white px-2 text-[10.5px] text-neutral-400">
          <Search className="size-3" />
          Search skills…
        </span>
        <HeaderBtn icon={Upload}>Upload Skill</HeaderBtn>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-5 pt-2.5">
        <span className="mr-0.5 text-[9.5px] font-medium text-neutral-400">
          Category
        </span>
        {groups.map(group => (
          <button
            key={group}
            type="button"
            onClick={() => setActiveGroup(group)}
            className={cn(
              'cursor-pointer rounded-full border px-2.5 py-1 text-[10px] font-medium transition-colors',
              group === activeGroup
                ? 'border-[#171717] bg-[#171717] text-white'
                : 'border-black/[0.1] bg-white text-neutral-600 hover:border-black/25',
            )}
          >
            {group}
          </button>
        ))}
      </div>

      <p className="shrink-0 px-5 pt-3 pb-2 text-[9.5px] font-semibold tracking-wide text-neutral-400 uppercase">
        Marketplace Skills · {visibleSkills.length}
      </p>

      {/* SkillCard grid — `skills-grid.tsx`: white→neutral gradient cards.
          The full 57-skill catalog, scrollable. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 [scrollbar-width:thin]">
        <div className="grid grid-cols-3 gap-2.5">
          {visibleSkills.map(skill => (
            <div
              key={skill.slug}
              className="flex flex-col overflow-hidden rounded-[10px] border border-black/[0.07] bg-gradient-to-b from-white to-neutral-50 shadow-[0px_1px_2px_rgba(0,0,0,0.04)]"
            >
              <div className="flex flex-1 flex-col gap-1 px-3 py-2.5">
                <p className="line-clamp-2 text-[11.5px] leading-4 font-medium tracking-tight text-[#171717]">
                  {skill.title}
                </p>
                <p className="flex min-w-0 items-center gap-1 text-[9px] text-neutral-400">
                  <span className="truncate font-mono">{skill.slug}</span>
                  <span>·</span>
                  <span className="shrink-0">{skill.category}</span>
                </p>
                <p className="line-clamp-2 text-[9.5px] leading-relaxed text-neutral-400">
                  {skill.description}
                </p>
              </div>
              <div className="flex items-center justify-between px-3 py-1.5">
                <span className="font-mono text-[8.5px] text-neutral-400">
                  {skill.version}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex items-center gap-1 text-[9.5px] font-medium text-neutral-400">
                    <CheckCircle2 className="size-3" />
                    Installed
                  </span>
                  <TrackedCtaLink
                    {...demoCta('Use skill')}
                    className="flex h-5.5 items-center gap-0.5 rounded-md bg-[#171717] px-2 text-[9.5px] font-medium text-white transition hover:opacity-85"
                  >
                    Use
                    <ArrowUpRight className="size-2.5" />
                  </TrackedCtaLink>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── Shared chrome pieces ─────────────────────────────────────────────── */

function RailItem({
  icon: Icon,
  label,
  active = false,
  collapsed = false,
  onClick,
  className,
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  active?: boolean
  collapsed?: boolean
  onClick?: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      title={collapsed ? label : undefined}
      onClick={onClick}
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-[5px] text-left text-[12px] font-normal transition-colors',
        collapsed && 'justify-center px-0',
        active
          ? 'bg-[oklch(0.375_0.007_75)] font-medium text-[oklch(0.98_0.004_80)]'
          : 'opacity-80 hover:bg-white/8 hover:opacity-100',
        className,
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      {collapsed ? null : label}
    </button>
  )
}
